// The company record as the ACCOUNT FILE: everything Cobrain holds on a company, on one page, in
// the order Mike chose for the call panel (module 15 G1 and G2; module 10 DESIGN's "company record
// as the account file", B864 to B871 and B896 to B905; the approved drawings in
// screens/2026-09-20-company-record/).
//
// Three rules this page keeps, each one a thing the old page broke:
//   - No source system and no decision code reaches the reader (G3, U32). Where a value came from
//     lives in the history.
//   - A section never shows a blank. It says what was read, and where a sister record holds the
//     missing piece it names that record.
//   - A section with no component of its own draws from a headline and a list of facts, so a
//     module that arrives later is on every company page the day it registers (PLAN §5).
//
// A section NEVER states a not-yet in fixed words. Owed each way, operations, and press and
// intelligence were each drawn from a literal — "None", "Nothing against this client", "Nothing
// about them" — and each of the three was wrong on the first company opened: the incident log held
// 23 rows naming Bet365 and the relevance matches 223, 62 of them with a consequence a brain wrote.
// Fixed words cannot tell "nothing happened" apart from "nothing was read", so every one of them
// now reads its own source and says which of the two it found.

import { OptionButton, useAccess, ActionInput, ActionSelect, ActionButton, CompanyLogo as Logo } from '../parts/index.js';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { records, ApiError, type CompanyRecordView, type CompanyFields, type EditableField, type InFrontRouteAnswer } from '../api';
import { tasks } from '../tasks/api';
import { useLoad, Loading, Failed, classWord, type PageProps } from './shared';
import { SeatsLine, UsagePanel } from './UsagePanel';
import { CompanyNotes } from './CompanyNotes';
import { conversationPath } from '../threads/api.js';
import { SummaryLines } from '../threads/SummaryLines.js';
import { whose, plateDay, type CompanyRead } from './company-read';
import { AlsoNeedsYou, useAlsoNeedsYou } from './AlsoNeedsYou';
import { LogoControl } from './LogoControl';
import { useCompanyReadings } from './OneTradeCharts';
import { InMotion, useCompanyDeals } from '../deals/CompanyDeals.js';
import { CloseToasts } from '../deals/CloseBox.js';
import { HeardCard } from '../deals/DealsPage.js';
import { opensPath, parkedWords } from '../deals/live-words.js';
import { BillingPanel } from './BillingPanel';
import {
  agreementsInForce, agreementsEnded, markIndistinguishable, briefOf, threadsOf, threadsRead, splitPeople, spanDays,
  clockTo, countWord, formatDay, plural, cleanName, feeLine, contractedAYear, totalLine, billedDetail,
  reconcile, comparedWords, endWords, noticeWords, noticeBy, type Bearing, timesWords, cardMoney, feeDetail, contractedRight, removedWords, paperLine, paperWords, owedFold, conversationsState, isWrittenThread,
  type Agreement, type Reconciliation,
} from './account';
import './account.css';
import { AccountBar } from './AccountBar';
import { matterKindWord, whenWord } from './shared.js';
import { dayMonth } from '../day.js';
import { ProductLedger, ProductSections } from './ProductLedger';
import { InFrontNote } from './InFrontNote';
import { InFrontEvidenceList } from './InFrontEvidence';
import { InFrontClientsSection, InFrontRecordLink, RightsHolderBar } from './RightsHolder';

const ROUTES: Array<{ v: string; label: string }> = [
  { v: 'igm_live', label: 'IGM Live' }, { v: 'genius', label: 'Genius' }, { v: 'both', label: 'Both' }, { v: 'integrating', label: 'Integrating' },
];

// api.ts belongs to another track; keep the newer company read fields local to this page.
type DealOrganisation = { organisationId?: string; organisationName?: string | null };
type CompanyTask = NonNullable<CompanyRecordView['tasks']>[number] & { createdAt?: string };
type CompanyWithDealDetails = Omit<CompanyRecordView, 'deals' | 'removedDeals' | 'tasks'> & {
  deals: Array<CompanyRecordView['deals'][number] & DealOrganisation & { signedOn?: string | null; contractId?: string | null }>;
  removedDeals: Array<CompanyRecordView['removedDeals'][number] & DealOrganisation>;
  tasks?: CompanyTask[];
};

/**
 * What the person has done sits HERE, above the load, because a save reloads the record and the
 * reload unmounts everything below it: the panel they were working in closed itself and the line
 * saying the change had landed went with it, so a save looked like nothing happening. Driven, and
 * that is exactly what happened.
 */
export function CompanyRecord({ store, open, id }: PageProps & { id: string }) {
  const [state, reload] = useLoad(() => records.company(store.tenantId, id), [store.tenantId, id]);
  // Closing a deal changes client status and the Closed state; read quietly so its toast survives.
  const [fresh, setFresh] = useState<{ original: CompanyRecordView; company: CompanyRecordView } | null>(null);
  const refresh = () => {
    if (state.kind !== 'ready') return;
    const original = state.data.company;
    void records.company(store.tenantId, id).then((r) => setFresh({ original, company: r.company })).catch(() => undefined);
  };
  const doing = useDoing();
  // BUGS.md T19 (module 04 B2199): a triage card's "Open the agreement" lands at the agreements (#a-agreements, a section of
  // their own since pipeline RC4; a link written before it, #a-deals, lands at Deals), once the record has drawn them, as
  // an item page's "Open the thread" lands on its evidence.
  useEffect(() => {
    const at = window.location.hash.slice(1);
    if (state.kind === 'ready' && (at === 'a-agreements' || at === 'a-deals')) document.getElementById(at)?.scrollIntoView({ block: 'start' });
  }, [state.kind]);
  if (state.kind === 'loading') return <Loading what="the company" />;
  if (state.kind === 'error') return <Failed message={state.message} />;
  return <Account store={store} open={open} c={fresh?.original === state.data.company ? fresh.company : state.data.company} reload={reload} refresh={refresh} doing={doing} />;
}

export type Doing = ReturnType<typeof useDoing>;

function useDoing() {
  const [changing, setChanging] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [wrong, setWrong] = useState<string | null>(null);
  const [answered, setAnswered] = useState<'accept' | 'keep' | null>(null);
  return { changing, setChanging, said, setSaid, wrong, setWrong, answered, setAnswered };
}

/* ------------------------------------------------------------------ the frame */

/** A section of the account file: a headline, one line of state, and a body. */
/** The page's index (brief section 4, U348): a link to each section, sticky under the bar; on a phone it scrolls. */
const INDEX: Array<[string, string]> = [['a-deals', 'Deals'], ['a-matters', 'Matters'], ['a-needs', 'Needs you'], ['a-brief', 'Where you stand'], ['a-products', 'By product'], ['a-infront-clients', "InFront's clients"], ['a-agreements', 'Agreements'], ['a-conv', 'Conversations'], ['a-notes', 'Notes'], ['a-owed', 'Owed'], ['a-people', 'People'], ['a-rights', 'Rights'], ['a-ops', 'Service'], ['a-press', 'Read'], ['a-record', 'Record']];
/**
 * The sections with nothing in them (BUGS.md D187), in page order, each with the word the one line says for it. Usage is
 * empty for a company that is not a client and has no linked account; a client's unlinked Usage is a thing to do.
 */
export function emptySections(
  c: Pick<CompanyRecordView, 'rights' | 'operations' | 'press' | 'usage' | 'counterpartyClass' | 'owed'>,
  n: { people: number; threads: number; owed: number; accountType: string | null },
): Array<{ id: string; word: string }> {
  // The sheet's account type decides where it has one (Eaton Gate Gaming is a prospect there and a client by class on
  // live, 7 Oct); the counterparty class where it has none.
  const client = isClientOf(n.accountType, c.counterpartyClass);
  const out: Array<{ id: string; word: string }> = [];
  if (!client && !c.usage.confirmed && c.counterpartyClass !== 'rights_holder' && c.counterpartyClass !== 'supplier') out.push({ id: 'a-usage', word: 'usage' });
  if (n.threads === 0) out.push({ id: 'a-conv', word: 'conversations' });
  if (n.owed === 0 && c.owed.length === 0) out.push({ id: 'a-owed', word: 'promises either way' });
  if (n.people === 0) out.push({ id: 'a-people', word: 'people' });
  if (c.rights.length === 0) out.push({ id: 'a-rights', word: 'rights' });
  if (!c.operations || c.operations.recent.length === 0) out.push({ id: 'a-ops', word: 'incidents' });
  if (!c.press || (c.press.named === 0 && (c.press.read?.length ?? 0) === 0)) out.push({ id: 'a-press', word: 'press' });
  return out;
}

/** A client by the sheet's account type where it has one, else by its counterparty class (the head's chip, D169). */
export const isClientOf = (accountType: string | null, counterpartyClass: string): boolean =>
  accountType ? accountType === 'client' : counterpartyClass === 'client';

/**
 * BUGS.md D11: what the company is to us, before its class: 'client' where Cobrain holds an agreement with it (a contract
 * on this record) and it is classed client or prospect, else the sheet's account type. Mike, 9 Oct 2026, by pop-up,
 * "Agreement wins (Recommended)": "Client wherever Cobrain holds an agreement with them, else your sheet. An agreement is
 * evidence; HubSpot is dropped." The API's records/standing.ts reads the same; a rights holder or a supplier with a
 * contract stays what its class says.
 */
export const standingOf = (accountType: string | null, counterpartyClass: string, agreements: number): string | null =>
  agreements > 0 && (counterpartyClass === 'client' || counterpartyClass === 'prospect') ? 'client' : accountType;

/** "a", "a or b", "a, b or c". */
const orList = (xs: string[]): string => (xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`);

function RecordIndex({ hide }: { hide: Set<string> }) {
  return (
    <nav className="aindex" aria-label="On this page">
      {INDEX.filter(([id]) => !hide.has(id)).map(([id, label]) => <a key={id} href={`#${id}`} onClick={(e) => { const el = document.getElementById(id); if (el) { e.preventDefault(); el.scrollIntoView({ block: 'start', behavior: 'smooth' }); } }}>{label}</a>)}
    </nav>
  );
}

function Section({ id, title, state, right, children }: { id: string; title: string; state?: ReactNode; right?: ReactNode; children: ReactNode }) {
  return (
    <section className="sec" id={id}>
      <div className="sh">
        <h2>{title}</h2>
        {state ? <span className="sl">{state}</span> : null}
        {right ? <span className="sr">{right}</span> : null}
      </div>
      <div className="sb">{children}</div>
    </section>
  );
}

/** BUGS.md D129: the rights whose roll-out lists this company, as "On the roll-out of: <property>", each opening its page. */
/** The addresses kept apart from the people, each with "This is a person" for a reader who may edit the record (BUGS.md D75). */
export function KeptApart({ apart, mayEdit, busy, onSay }: { apart: ReadonlyArray<{ id: string; name: string }>; mayEdit: boolean; busy: boolean; onSay: (id: string) => void }) {
  if (apart.length === 0) return null;
  return (
    <details className="shared">
      <summary>{plural(apart.length, 'shared address')} — a desk or a mailbox, not a person</summary>
      <div className="addrs">{apart.map((s) => (
        <span className="addr" key={s.id}>{s.name}{mayEdit ? <> <ActionButton type="button" className="hx-link" disabled={busy} aria-label={`This is a person: ${s.name}`} onClick={() => onSay(s.id)}>This is a person</ActionButton></> : null}</span>
      ))}</div>
    </details>
  );
}

/** The reader's own "This is a person" under the person, with its Undo while the week runs (BUGS.md D75), worded as
 *  Deals' Undo is (Mike, FT2, 6 Oct: "Undo until 14 Oct"). */
export function SaidPerson({ said, busy, onUndo }: { said: { actionId: string; until: string }; busy: boolean; onUndo: (actionId: string) => void }) {
  return <span className="said-person">You said this is a person. <ActionButton type="button" className="hx-link" disabled={busy} onClick={() => onUndo(said.actionId)}>{`Undo until ${dayMonth(new Date(said.until))}`}</ActionButton></span>;
}

export function OnRollOut({ rollOuts, open }: { rollOuts: NonNullable<CompanyRecordView['rollOuts']>; open: (path: string) => void }) {
  if (rollOuts.length === 0) return null;
  return (
    <p className="none" style={{ marginTop: 8 }}>
      <b>On the roll-out of:</b>{' '}
      {rollOuts.map((r, i) => <span key={r.propertyId}>{i ? '; ' : ''}<ActionButton reading type="button" className="go" onClick={() => open(`/rights/${r.propertyId}`)}>{r.propertyName}</ActionButton></span>)}
    </p>
  );
}

/** The company's matters under its deals (BUGS.md D116): each its name, opening its page, its kind and when it last moved. */
export function CompanyMatters({ matters, open, now }: { matters: CompanyRecordView['matters']; open: (path: string) => void; now?: number }) {
  if (matters.length === 0) return null;
  return (
    <Section id="a-matters" title="Matters" state={`${countWord(matters.length)} on file`}>
      <ul className="mt-list">
        {matters.map((m) => (
          <li key={m.id}>
            <ActionButton reading type="button" className="go" onClick={() => open(`/matters/${m.id}`)}>{m.title}</ActionButton>
            <span className="note"> {matterKindWord(m.kind)}{m.lastMovementAt ? `, last moved ${whenWord(m.lastMovementAt, now)}` : ''}</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

export type Fact = { key: string; value: string; evidence?: string; none?: boolean };

/** A section with no component of its own, drawn from its headline and its facts (PLAN §5). */
function Facts({ id, title, state, right, facts }: { id: string; title: string; state?: string; right?: string; facts: Fact[] }) {
  return (
    <section className="sec gen" id={id}>
      <div className="sh"><h2>{title}</h2>{state ? <span className="sl">{state}</span> : null}{right ? <span className="sr">{right}</span> : null}</div>
      <div className="sb"><div className="facts">
        {facts.map((f) => (
          <div className="f" key={f.key}>
            <span className="fk">{f.key}</span>
            <span className={f.none ? 'fv no' : 'fv'}>{f.value}</span>
            {f.evidence ? <span className="fe">{f.evidence}</span> : null}
          </div>
        ))}
      </div></div>
    </section>
  );
}

/** Nothing here, and what was read to know it. */
function Nothing({ children, read }: { children: ReactNode; read?: string }) {
  return <p className="none">{children}{read ? <span className="read">{read}</span> : null}</p>;
}

/* ------------------------------------------------------------------- the page */

function Account({ store, open, c, reload, refresh, doing }: PageProps & { c: CompanyWithDealDetails; reload: () => void; refresh: () => void; doing: Doing }) {
  const access = useAccess();
  const admin = ['admin', 'super_admin'].includes(access?.person.role ?? '');
  const [fields, setFields] = useState<CompanyFields>(c);
  const [busy, setBusy] = useState(false);
  const { changing, setChanging, said, setSaid, wrong: error, setWrong: setError, answered, setAnswered } = doing;
  const now = new Date();
  // The deals in motion as Deals draws them (pipeline P5 step 6, U240): the same read, line and card.
  const live = useCompanyDeals(store.tenantId, c.id, open, refresh);
  // What the last answer to a heard proposal on this record did ("Accepted: ..."), as Deals says it.
  const [heardSaid, setHeardSaid] = useState<string | null>(null);
  // BUGS.md D185: People shows its first five, and the rest on a press; and D166: Conversations its newest five threads.
  const [allPeople, setAllPeople] = useState(false);
  const [allThreads, setAllThreads] = useState(false);
  // BUGS.md D75: "This is a person" on a shared address, and its Undo for a week; the record is read again after either.
  const [saying, setSaying] = useState(false);
  const [sayWrong, setSayWrong] = useState<string | null>(null);
  const sayPerson = async (run: () => Promise<unknown>) => {
    setSaying(true); setSayWrong(null);
    try { await run(); reload(); }
    catch (e) { setSayWrong(`That did not go through: ${e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e)}.`); }
    finally { setSaying(false); }
  };
  // The logo (U359, U371): RC7's stored one where the read carries it, set or replaced here by hand.
  const [logo, setLogo] = useState(c.read?.logo ?? null);
  const rc7 = c.read !== null && c.read !== undefined && c.read.logoSearch !== undefined;
  // Also needs you (brief section 4 item 6): the reader's triage items about the company or a brand under it.
  // The charts' readings (RC2), fetched after the head draws, as track A's usage route returns them.
  // RC14: a note that the client went direct to InFront, set or undone here, stands over the read until the next load.
  const [route, setRoute] = useState<InFrontRouteAnswer | null>(null);
  // RC14 on RC10: the evidence as a confirm, dismiss or undo here left it.
  const [evidence, setEvidence] = useState<NonNullable<CompanyRead['inFrontEvidence']> | null>(null);
  const routed = c.read && route ? { ...c.read, inFrontRoute: route.inFrontRoute, readings: { ...c.read.readings, inFront: route.inFront } } : c.read;
  const read = routed && evidence ? { ...routed, inFrontEvidence: evidence } : routed;
  const readings = useCompanyReadings(store.tenantId, c.id, c.read ? Object.values(c.read.readings).some((x) => x.state === 'read') : false);
  const [needsLoad, needsGone, needsUpdate] = useAlsoNeedsYou(store, new Set([c.id, ...c.children.map((k) => k.id)]));
  const needs = needsLoad.kind === 'ready' ? { count: needsLoad.items.length, headlines: needsLoad.items.map((i) => i.headline) } : null;
  // BUGS.md D192, the record's half: the browser tab says whose record it is, and says what it said before on leaving.
  useEffect(() => { const was = document.title; document.title = c.name; return () => { document.title = was; }; }, [c.name]);

  const save = async (field: EditableField, value: string | null, then?: () => Promise<void>) => {
    setBusy(true); setError(null);
    try {
      const r = await records.editCompany(store.tenantId, c.id, field, value);
      setFields(r);
      if (then) await then();
      setSaid('Saved. The change is on the record with your name against it.');
      setChanging(true);
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };
  const answer = async (a: 'accept' | 'keep') => {
    if (!c.proposal?.judgementId) return;
    setBusy(true); setError(null);
    try {
      const r = await records.answerProposal(store.tenantId, c.id, c.proposal.judgementId, a);
      if (r.owner) setFields((f) => ({ ...f, owner: r.owner! }));
      setAnswered(a);
      setSaid(null);   // one line about what just happened, not a stack of them
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const go = (path: string, label: string) => <ActionButton reading type="button" className="go" onClick={() => open(path)}>{label}</ActionButton>;

  const inForce = markIndistinguishable(agreementsInForce(c, now));
  // Agreements that ran to their end date and did not renew by themselves (U325, BUGS.md D7): drawn in grey under
  // their own band, and counted with the ended deals, never as in force.
  const endedAgreements = agreementsEnded(c, now);
  // BUGS.md D9: a HubSpot deal put aside to be sorted by hand, or archived (U222), is not in motion; it is named apart, in
  // Deals' own words.
  const inMotion = c.deals.filter((d) => (d.lifecycle === 'open' || d.lifecycle === 'paused') && !d.putAside);
  const toSort = c.deals.filter((d) => d.putAside === 'to_sort');
  const archivedDeals = c.deals.filter((d) => d.putAside === 'archived');
  // BUGS.md D52: a parked deal in the sentence says so first, in Deals' own words ("Parked till 19 Oct: <why>"; Mike by
  // pop-up on 5 Oct, "Everywhere, ring included"), so it does not read as live while the Deals read loads or fails.
  const motionState = (d: CompanyRecordView['deals'][number]): string => { const p = parkedWords(d); return p ? `${p.words}, ${d.stage}` : d.stage; };
  // A deal removed from the Deals desk (U156) is still listed, with the ended ones, marked removed with the date (Mike by
  // pop-up, 2026-09-24). Not of interest is lifecycle `declined`: this line read 'not_of_interest', which is no
  // lifecycle, so a declined deal was on neither line.
  // BUGS.md D148: a declined deal says so, in Deals' word for a decline (U153, "Not of interest"), with the reason Cobrain
  // holds, so it no longer reads the same as a lost one; a lost deal keeps its stage.
  const endedState = (d: CompanyRecordView['deals'][number]): string => {
    if (d.lifecycle !== 'declined') return d.stage;
    const why = d.stateReason?.trim();
    return why ? `Not of interest: ${why}` : 'Not of interest';
  };
  const brandPrefix = (d: DealOrganisation): string => d.organisationId !== c.id && d.organisationName ? `${d.organisationName}: ` : '';
  const ended = [
    ...c.deals.filter((d) => (d.lifecycle === 'lost' || d.lifecycle === 'declined') && !d.putAside).map((d) => ({ ...d, stage: endedState(d), removed: null })),
    ...(c.removedDeals ?? []).map((d) => ({ ...d, removed: removedWords(d.removedAt, d.removedBy) })),
  ];
  // Drawn as lines once the Deals read is in; what it does not draw (a deal parked, or put aside to be sorted) keeps the
  // sentence, and until it is in, or if it fails, the sentence says them all.
  const lines = live.kind === 'ready' && live.rows.length > 0 ? live : null;
  const drawn = new Set(lines ? lines.rows.map((r) => r.id) : []);
  const motionRest = inMotion.filter((d) => !drawn.has(d.id));
  const motionCount = (lines ? lines.rows.length : 0) + motionRest.length;
  // The Deals section's other three groups (brief section 4 item 5, U350). The deal on another company's record that this
  // company's money depends on is the risk line's own (RC1): Bet365's forecast rests on the Modus rights, held on MODUS
  // Online's record. "To confirm or open" is what Cobrain heard about this company or its brands that is not yet a deal,
  // drawn with Deals' own heard card; a proposal the read names that the Deals read did not carry is named in a line.
  const heat = live.kind === 'ready' ? live.heat : null;
  const risk = c.read?.risk ?? null;
  const dep = risk?.deal && !risk.deal.onThisRecord ? risk.deal : null;
  const depRow = dep && heat ? heat.lines.find((r) => r.id === dep.id) ?? null : null;
  const depMeta = risk ? `${whose(c.name)} forecast rests on ${risk.agreement}` : '';
  const ownIds = new Set([c.id, ...(c.read?.company.brands.map((b) => b.id) ?? [])]);
  const heardHere = heat ? heat.heard.filter((h) => h.organisationId !== null && ownIds.has(h.organisationId)) : [];
  const heardElse = heat ? (c.read?.money.potential.proposals ?? []).filter((p) => !heardHere.some((h) => h.id === p.id)) : [];
  const toConfirm = heardHere.length + heardElse.length;
  const signed = c.deals.filter((d) => d.lifecycle === 'won' && !d.putAside);
  const dealsState = [
    motionCount ? `${motionCount} open` : 'nothing open',
    dep ? `1 open that ${whose(c.name)} money depends on` : null,
    toConfirm ? `${toConfirm} to confirm or open` : null,
    signed.length ? `${signed.length} signed` : null,
  ].filter(Boolean).join(', ').replace(/^n/, 'N');
  const brief = briefOf(c, now);
  // BUGS.md D166: the threads as the API read them whole, where it does; an older API's newest messages otherwise.
  const conv = c.threads ? threadsRead(c.threads, c.recentTotal) : threadsOf(c.recent, c.recentTotal);
  // BUGS.md D154: a call or a meeting is not a written thread ('they wrote last'); it is listed under Meetings, from the
  // meetings Cobrain holds with the company (c.meetings), never in fixed words.
  const written = conv.threads.filter((t) => isWrittenThread(t.kind));
  const calls = c.meetings ?? { total: 0, rows: [] };
  // BUGS.md D10: the meetings with them at a show, with the show and the day (Mike by pop-up, 8 Oct about 12:37 CEST:
  // "list it under the record's Meetings with the show and the day").
  const shows = c.meetings?.shows ?? [];
  const diary = conv.threads.filter((t) => t.kind === 'calendar_event');
  const { people, shared } = splitPeople(c.people);
  // The addresses kept apart: those whose name says desk or mailbox, and those the nightly pass filed so (BUGS.md D75).
  const apart = [...shared.map((x) => ({ id: x.id, name: x.name })), ...(c.filedApart ?? []).map((x) => ({ id: x.id, name: cleanName(x.name) }))]
    .sort((a, b) => a.name.localeCompare(b.name, 'en-GB'));
  const peopleMore = c.peopleTotal !== undefined && c.peopleTotal > c.people.length;
  const soonest = inForce.find((a) => a.clock && a.clock.days >= 0) ?? null;
  const owner = fields.owner.value;
  const money = c.billing.available && c.billing.confirmed ? c.billing : null;
  // Owed each way, split by who made the promise (see the API's comment on `owed`).
  const ours = c.owed.filter((w) => !w.theirs);
  const theirs = c.owed.filter((w) => w.theirs);
  // Cobrain's unaccepted readings more than a month past their date fold away (D16), so the heading counts only the
  // late rows still in sight; the fold line carries its own count.
  const oursFold = owedFold(ours, now);
  const theirsFold = owedFold(theirs, now);
  const overdue = [...oursFold.shown, ...theirsFold.shown].filter((w) => w.dueAt !== null && w.dueAt < iso(now));
  // The heading's figures are the API's counts of every open promise, not of the forty rows listed (BUGS.md D37).
  const owedTotal = c.owedCounts ? c.owedCounts.ours + c.owedCounts.theirs : c.owed.length;
  // BUGS.md D89: the open tasks filed on the company, under What we owe them, each marked as a task.
  const tasks = c.tasks ?? [];
  // BUGS.md D191: the promises folded away (Cobrain's readings more than a month past their date) are past their date
  // too, and the heading counts them; the API's count leaves them out.
  const late = (c.owedCounts ? c.owedCounts.late : overdue.length) + oursFold.folded.length + theirsFold.folded.length;
  const unbilled = inForce.filter((a) => !a.billed).length;
  // BUGS.md D187: a section with nothing in it is a word in one line ("Nothing yet on conversations, people or press"),
  // not a page of nothing, and Usage is no part of a rights holder's or a supplier's record. A client's Usage stays
  // even unlinked, since linking it is a thing to do.
  const standing = standingOf(fields.accountType.value, c.counterpartyClass, c.contracts.length);
  const empty = emptySections(c, { people: people.length + apart.length, threads: conv.threads.length + calls.total + shows.length, owed: owedTotal + tasks.length, accountType: standing });
  const isEmpty = (id: string) => empty.some((e) => e.id === id);
  const usageHidden = !isClientOf(standing, c.counterpartyClass) && !c.usage.confirmed
    && (c.counterpartyClass === 'rights_holder' || c.counterpartyClass === 'supplier');
  // The months finance ACTUALLY billed, so a client eight months old is not reported as three times
  // overstated. billedLast12 is the sum of exactly these months.
  const monthsBilled = money ? money.months.filter((m) => m.billed !== null).length : 0;
  // Only what this company is contracted to pay US is weighed against what finance booked. An
  // agency row is the rights holder's gross, and finance books our share of it against them, so
  // including it made the page say the register was six times over -- and, after the season fee was
  // mended, that £504,000 was "not borne out" when £492,000 of it was never meant to be (B1534).
  const worth = reconcile(inForce.filter((a) => a.season === null), money?.billedLast12 ?? null, monthsBilled);
  // Whether finance's own figures bear the register out. Nothing on this page states a register
  // fee as money unless they do (A37).
  const ok = corroborated(worth);
  const [adding, setAdding] = useState(false);
  // BUGS.md D51: an agreement's reminder with no notice date still to come opens this form with the agreement named, as
  // "Add a task for this company" opens it, so the person gives the day.
  const [addTitle, setAddTitle] = useState('');
  /**
   * How many agreement cards start open is dictated by how many the company has, which is Mike's
   * point 6: most companies carry one or two and should simply be read, and only a company with a
   * shelf of them needs collapsing. Three or fewer open together; more than that and only the one
   * running out soonest opens, the rest being a click each. Every card opens either way -- before
   * this the first was open and the others could not be opened at all.
   */
  const [openDeals, setOpenDeals] = useState<Set<string>>(() => {
    const rows = markIndistinguishable(agreementsInForce(c, now));
    if (rows.length === 0) return new Set();
    if (rows.length <= 3) return new Set(rows.map((a) => a.id));
    const first = rows.find((a) => a.clock && a.clock.days >= 0) ?? rows[0]!;
    return new Set([first.id]);
  });

  return (
    <div className="acct">

      {/* ------------------------------------------------- the head and the decision */}
      <section className="ahead" id="a-head">
        <p className="crumb">{go('/companies', 'Companies')}</p>
        <div className="aname">
          {/* The logo's place (U359, U371): track D's Logo, which draws the stored logo once pipeline RC7 keeps one and the
              company's initials until then, as every company line does. */}
          <Logo name={c.name} logo={logo?.url ?? null} />
          <h1>{c.name}</h1>
          {/* BUGS.md D169: one word set, from the account type where the company has one, else its counterparty class
              (Client, Prospect, Rights holder, Supplier), as the Companies list says it; never HubSpot's status. BUGS.md
              D11: an agreement on file makes it a Client first (standingOf). */}
          <span className="kind">{standing === 'client' ? 'Client' : standing === 'prospect' ? 'Prospect' : classWord(c.counterpartyClass)}</span>
        </div>
        {rc7 && c.mayEdit ? <LogoControl storeId={store.tenantId} companyId={c.id} logo={logo} onLogo={setLogo} /> : null}
        {/* BUGS.md D181: each fact with its label, as The record names it, never a bare value; and D188: said here once,
            so The record below lists only what the head does not. */}
        <div className="afacts">
          {c.companyType ? <span>What they are: <b>{c.companyType}</b></span> : null}
          {c.companyTier ? <span>Brand tier: <b>{c.companyTier}</b></span> : null}
          {fields.accountTier.value ? <span>Account tier: <b>{fields.accountTier.value}</b></span> : null}
          {fields.hq.value.market ? <span>Home market: <b>{fields.hq.value.market}</b></span> : null}
          <span>{c.domains.length ? <>Mail domain{c.domains.length === 1 ? '' : 's'}: <b>{c.domains.join(', ')}</b></> : 'Mail domain: none'}</span>
          <span>{c.parent ? <>Under {go(`/companies/${c.parent.id}`, c.parent.name)}</> : c.children.length ? `${countWord(c.children.length)} ${c.children.length === 1 ? 'brand' : 'brands'} under it` : 'No parent, no brands under it'}</span>
          {c.read ? <span>{owner ? <>Handled by <b>{owner.name ?? 'somebody'}</b></> : 'No one handles them'}</span> : null}
        </div>
        {/* BUGS.md D98: the company's own note says so on its face, as module 15 G11 asked of the duplicates it made. */}
        {c.notes ? <p className="anote">Note: {c.notes}</p> : null}
        {/* The account bar (RC4): billed, forecast, potential and dates, the risk line and the verdicts, all in the one
            company read's own words (U352). Where the read is absent (an older API, the personal store) the head
            keeps the sentence it had. */}
        {/* A company typed Rights Holder (RC13, RC16; c5-states.html): the supply side. Where finance holds no line for it
            as a client (MODUS Online) it stands in the account bar's place; where it is a client too (Infront), under it. */}
        {read && !(read.rightsHolder && !read.money.available) ? <AccountBar read={read} open={open} needs={needs} addDeal={c.mayEdit ? `/deals/new?company=${encodeURIComponent(c.id)}` : null} /> : null}
        {read?.rightsHolder ? <RightsHolderBar rh={read.rightsHolder} titled={read.money.available} needs={read.money.available ? null : needs} /> : null}
        {c.read ? null : <p className="ahandled">
          {owner ? <><b>{owner.name ?? 'Somebody'}</b> handles them. </> : <>Nobody handles them. </>}
          {inForce.length
            ? <>{countWord(inForce.length)} {inForce.length === 1 ? 'agreement' : 'agreements'} in force. <Worth r={worth} /></>
            : endedAgreements.length
              ? <b>No agreement in force.</b>
              : <b>Cobrain holds no agreement for this company.</b>}
        </p>}

        <p className="aq">What would you like to do?</p>
        <div className="aopts">
          {/* A brief is offered only when there is a call to be briefed for. It used to be the lead
              option on every company, and Mike's question was the right one: if there is no call,
              what is the brief about? */}
          {c.nextMeeting
            /* BUGS.md D184: no number legend; this page has no key that presses an option. */
            ? <OptionButton opensOnly variant="lead" note={c.nextMeeting.subject} onClick={() => open(`/calendar?event=${encodeURIComponent(c.nextMeeting!.id)}&day=${encodeURIComponent(c.nextMeeting!.at)}`)}>Brief me before the call on {formatDay(c.nextMeeting.at)}</OptionButton>
            : null}
          {/* This used to say "Open the agreement ending 31 December" on a company with four of
              them, two of which end on that very date: it singled one out for no reason a reader
              could see. It now names the set, and only says "the one" when there is one. */}
          {inForce.length
            ? <OptionButton opensOnly type="button" onClick={() => document.getElementById('a-agreements')?.scrollIntoView({ block: 'start', behavior: 'smooth' })}>
                {inForce.length === 1 ? 'Open the agreement' : `See the ${countWord(inForce.length).toLowerCase()} agreements`}
              </OptionButton>
            : null}
          {/* "Add a task" navigated to the task LIST, losing the company. It now writes the task
              against this company without leaving the page. */}
          <OptionButton type="button" onClick={() => { setAddTitle(''); setAdding((v) => !v); }} aria-expanded={adding}>
            {adding ? 'Never mind' : 'Add a task for this company'}
          </OptionButton>
        </div>
        {adding ? <AddTask key={addTitle} companyId={c.id} companyName={c.name} store={store} title={addTitle}
          deals={inMotion.map((d) => ({ id: d.id, title: d.matterTitle }))}
          onDone={(said) => { setAdding(false); setAddTitle(''); doing.setSaid(said); }} /> : null}
        {doing.said ? <p className="asaid" role="status">{doing.said}</p> : null}
        {c.read ? <RecordIndex hide={new Set([...empty.map((e) => e.id), ...(needs && needs.count ? [] : ['a-needs']), ...(read?.rightsHolder?.inFrontClients ? [] : ['a-infront-clients']), ...(c.matters.length ? [] : ['a-matters'])])} /> : null}
      </section>

      {/* ------------------------------------------------------------------- the deals */}
      {/* Directly under the head (brief section 4 item 5, U350; drawing a6-account-file.html), in the Deals page's own
          lines and in its four groups: open; open on another company's record that this company's money depends on; to
          confirm or open; signed. The agreements are a section of their own below. */}
      <Section id="a-deals" title="Deals" state={dealsState}>
        {live.kind === 'ready' ? <CloseToasts ctx={live.ctx} /> : null}
        {lines ? <InMotion rows={lines.rows} ctx={lines.ctx} accountId={lines.accountId} companyName={c.name} title="Open" /> : null}
        {lines && motionRest.length > 0
          ? (
            <p className="none" style={{ marginTop: 8 }}>
              <b>Also open:</b>{' '}
              {motionRest.map((d, i) => <span key={d.id}>{i ? '; ' : ''}{go(`/deals/${d.id}`, d.matterTitle)}, {motionState(d)}</span>)}
            </p>
          )
          : null}
        {/* "Add a product" under "In motion" opens Add deal with this company set (the Deal work lane's W6, U286). */}
        {live.kind === 'ready' && live.ctx.mayEdit
          ? <p className="dw-addlink"><ActionButton type="button" className="hx-link" onClick={() => open(`/deals/new?company=${encodeURIComponent(c.id)}`)}>Add a product</ActionButton></p>
          : null}
        {lines ? null : (
          <p className="none" style={{ marginTop: 12 }}>
            <b>Open:</b>{' '}
            {inMotion.length === 0
              ? <>nothing. No conversation Cobrain has read is about a new agreement with them.<span className="read">{c.recentTotal ? `Read: ${plural(c.recentTotal, 'message')} with this company.` : 'There is no message with this company to read.'}</span></>
              : inMotion.map((d, i) => <span key={d.id}>{i ? '; ' : ''}{go(`/deals/${d.id}`, d.matterTitle)}, {motionState(d)}</span>)}
          </p>
        )}
        {dep ? (
          depRow && heat && live.kind === 'ready'
            ? <InMotion rows={[depRow]} ctx={live.ctx} accountId={depRow.accountId} companyName={dep.organisationName} title={`Open, on ${whose(dep.organisationName)} record`} meta={depMeta} openFirst={false} />
            : (
              <>
                <div className="hx-bandline"><h3>Open, on {whose(dep.organisationName)} record</h3><span className="hx-meta">{depMeta}</span></div>
                <p className="none">{go(`/deals/${dep.id}`, dep.title)}, at {dep.stage}{dep.ownerName ? `, held by ${dep.ownerName}` : ', with no owner'}</p>
              </>
            )
        ) : null}
        {toConfirm > 0 || heardSaid ? (
          <>
            <div className="hx-bandline"><h3>To confirm or open</h3><span className="hx-cnt">{toConfirm}</span><span className="hx-meta">heard by Cobrain, not yet deals</span></div>
            {heardSaid ? <p className="dl-said" role="status">{heardSaid}</p> : null}
            {heat ? heardHere.map((h) => <HeardCard key={h.id} h={h} store={store.tenantId} me={heat.me} team={heat.team ?? []} open={open} onAnswered={(w) => { setHeardSaid(w); if (live.kind === 'ready') live.ctx.refresh(); reload(); }} />) : null}
            {heardElse.length > 0 ? <p className="none"><b>Also heard:</b> {heardElse.map((p) => p.headline.trim()).join(', ')}. Each is answered on Deals.</p> : null}
          </>
        ) : null}
        {signed.length > 0 ? (
          <>
            <div className="hx-bandline"><h3>Signed</h3><span className="hx-cnt">{signed.length}</span></div>
            <p className="none">{signed.map((d, i) => <span key={d.id}>{i ? '; ' : ''}{go(`/deals/${d.id}`, d.matterTitle)} <b>Won</b>{d.signedOn ? ` ${formatDay(d.signedOn)}` : ''}{d.contractId ? <> · {go(`/contracts/${d.contractId}`, 'Contract')}</> : <small>{d.contractDueAt && Date.parse(d.contractDueAt) <= Date.now() ? ' Contract to come, 30 days.' : ' Contract to come'}</small>}</span>)}</p>
          </>
        ) : null}
        {toSort.length || archivedDeals.length ? (
          <p className="none" style={{ marginTop: 8 }}>
            {toSort.length ? <><b>Put aside to be sorted by hand:</b> {toSort.map((d, i) => <span key={d.id}>{i ? '; ' : ''}{brandPrefix(d)}{go(`/deals/${d.id}`, d.matterTitle)}</span>)}.{archivedDeals.length ? ' ' : ''}</> : null}
            {archivedDeals.length ? <><b>Archived:</b> {archivedDeals.map((d, i) => <span key={d.id}>{i ? '; ' : ''}{brandPrefix(d)}{go(`/deals/${d.id}`, d.matterTitle)}</span>)}.</> : null}
          </p>
        ) : null}
        <p className="none" style={{ marginTop: 8 }}>
          <b>Ended:</b>{' '}
          {ended.length === 0 ? 'none.'
            : ended.map((d, i) => <span key={d.id}>{i ? '; ' : ''}{brandPrefix(d)}{d.removed ? <>{d.matterTitle}, {d.removed}</> : <>{go(`/deals/${d.id}`, d.matterTitle)}, {d.stage}</>}</span>)}
        </p>
        {/* BUGS.md D129 (Mike by pop-up, 8 Oct 2026, "All as recommended (Recommended)": "an operator's record gets 'On the
            roll-out of: <property>'"): the rights whose roll-out lists this company, each opening its right's page. */}
        <OnRollOut rollOuts={c.rollOuts ?? []} open={open} />
      </Section>

      {/* ---------------------------------------------------------------- the matters */}
      {/* BUGS.md D116 (Mike by pop-up, 8 Oct 2026, "All as recommended (Recommended)": "A company record gets a 'Matters'
          list under its deals: each matter's name opens its page, with its kind and when it last moved."). The read sent
          them and nothing drew them, so a won or put-aside deal's matter could not be reached from its company. None, no
          section. */}
      <CompanyMatters matters={c.matters} open={open} />

      {/* -------------------------------------------- also needs you (brief section 4 item 6) */}
      <AlsoNeedsYou store={store} name={c.name} load={needsLoad} onGone={needsGone} onUpdate={needsUpdate} open={open} />

      {/* ------------------------------------------------------------------ the brief */}
      <section className="brief" id="a-brief">
        {/* BUGS.md D186: "read just now" was a fixed string over sources a day or more old; it goes. */}
        <div className="bh"><h2>Where you stand</h2></div>
        {brief.lines.length ? (
          <ol>
            {brief.lines.map((l) => (
              <li key={l.label}>
                <span className="bl">{l.label}</span>
                <p className="bt">{l.text}{l.warmWord ? <em>{l.warmWord}</em> : null}{l.warmWord ? '.' : ''}</p>
                {l.under ? <span className="bs">{l.under}</span> : null}
              </li>
            ))}
          </ol>
        ) : <ol><li><span className="bl">What is here</span><p className="bt">There is nothing to say about this company yet.</p></li></ol>}
        {brief.foot.length ? <p className="bfoot">{sentence(brief.foot)}.</p> : null}
      </section>

      {/* ------------------------------------------- by product, then each product (RC4 stage 3) */}
      {/* The ledger and a section per product read (brief section 4 items 7 and 8), then the measures under them, the
          Usage panel; with no read (the personal store, an older API) the Usage panel stands alone as before. */}
      {read ? <ProductLedger read={read} service={c.operations?.byProduct ?? null} /> : null}
      {read ? <ProductSections read={read} readings={readings} name={c.name}
        seats={<SeatsLine usage={c.usage} />}
        inFrontNote={<>
          <InFrontNote storeId={store.tenantId} companyId={c.id} name={c.name} read={read} readings={readings} mayEdit={c.mayEdit} onNoted={setRoute} />
          <InFrontEvidenceList storeId={store.tenantId} companyId={c.id} name={c.name} read={read} mayEdit={c.mayEdit} open={open} onChange={setEvidence} />
          <InFrontRecordLink read={read} open={open} />
        </>} /> : null}
      {read?.rightsHolder?.inFrontClients ? <InFrontClientsSection clients={read.rightsHolder.inFrontClients} open={open} /> : null}
      {/* REQ-2026-10-07-167: Usage is gone, Mike, 7 Oct: "There is also a section 'usage' that is now defunct with the new
          design". OneTrade's section reads people, hours and the pattern, and carries the seats sentence (his pick by
          pop-up, "Move it into OneTrade"); what stays is a client's unlinked account, a thing to do, or the whole panel
          where no product read came (the personal store, an older API). */}
      {usageHidden || isEmpty('a-usage') || (read && c.usage.confirmed) ? null : <UsagePanel usage={c.usage} name={c.name} isAdmin={admin} open={open} />}

      {/* ------------------------------------------------------------- the agreements */}
      {/* Every agreement on file, in force and ended, as the Deals section carried them until RC4 put Deals under the head
          alone. Brief section 4 item 9 makes this "Agreements and money", built in a later stage of the rebuild. */}
      <Section id="a-agreements" title="Agreements"
        state={`${inForce.length ? `${countWord(inForce.length)} in force` : 'Nothing in force'}, ${endedAgreements.length ? `${countWord(endedAgreements.length).toLowerCase()} ended` : 'none ended'}`}
        right={contractedRight(inForce, ok, money?.billedLast12 ?? null)}>
        <div className="hx-bandline lv-force"><h3>In force</h3><span className="hx-meta">every agreement on file</span></div>
        {inForce.length === 0 ? (
          <Nothing read="Read: every agreement on file and every billing line joined to this company.">No agreement in force.</Nothing>
        ) : (
          <div className="rows">
            {inForce.map((a, i) => (
              <div className={openDeals.has(a.id) ? 'row open' : 'row'} key={a.id}>
                <ActionButton reading type="button" className="rt rt-open" aria-expanded={openDeals.has(a.id)}
                  onClick={() => setOpenDeals((was) => { const next = new Set(was); if (next.has(a.id)) next.delete(a.id); else next.add(a.id); return next; })}>
                  {a.title}
                </ActionButton>
                <span className="rw">{a.clock ? <span className={a.clock.warm ? 'clock warm' : 'clock'}>{a.clock.words}</span> : <span className="clock">No end date</span>}</span>
                <span className="rm">
                  {/* One that renewed by itself says so, and the date it renews next (U326). */}
                  {a.renewed ? <span>Renewed by itself on {formatDay(a.renewed.on)}</span> : null}
                  {/* 'Ends' or 'Renews by itself' from the register's own word, never from whether it has renewed (D160). */}
                  {a.endDate
                    ? <span>{endWords(a).label} {formatDay(a.endDate)}{endWords(a).note ? <>, {endWords(a).note}</> : null}</span>
                    : <span>No end date on record</span>}
                  {(() => { const m = cardMoney(a, ok); return <span className={m.untrusted ? 'untrusted' : undefined}>{m.untrusted ? m.text : <b>{m.text}</b>}</span>; })()}
                  {a.twin ? <span><b>{a.twin === 1 ? `One of ${countWord(a.twins).toLowerCase()}, identical in everything Cobrain holds but their reference` : `The ${a.twin === 2 ? 'second' : 'next'} of the ${countWord(a.twins).toLowerCase()}`}</b></span> : null}
                  {a.booked
                    ? <span>Finance booked <b>£{Math.round(a.booked.amount).toLocaleString('en-GB')}</b> against it{a.entity ? <>, {a.entity}</> : null}</span>
                    : a.entity ? <span>Billed by <b>{a.entity}</b>, with no line of its own</span> : <span>No billing line of its own</span>}
                </span>
                {openDeals.has(a.id) ? (
                  <>
                    <div className="terms">
                      {a.renewed
                        ? <Term label="Term" value={a.start ? `${formatDay(a.start)} → ${formatDay(a.renewed.firstEnd)}` : `To ${formatDay(a.renewed.firstEnd)}`} words sub="its first term; it has renewed by itself since" />
                        : <Term label="Term" value={a.start && a.endDate ? `${formatDay(a.start)} → ${formatDay(a.endDate)}` : 'Not recorded'} words={Boolean(a.start && a.endDate)} none={!(a.start && a.endDate)} sub={a.start ? undefined : 'No start date is on record for it'} />}
                      <Term label={endWords(a).label} value={a.endDate ? formatDay(a.endDate) : 'Not recorded'} words={!a.endDate}
                        sub={a.renewed
                          ? a.renewed.termMonths
                            ? `${a.clock ? `${a.clock.words}, ` : ''}worked out from its ${a.renewed.termMonths}-month first term`
                            : 'the register gives no term to work out the next date from'
                          : [a.clock?.words, endWords(a).note].filter(Boolean).join(', ') || undefined} />
                      {(() => { const n = noticeWords(a, now); return <Term label="Notice" value={n.value} words none={n.none} sub={n.sub} />; })()}
                      <Term
                        label="Fee"
                        {...feeDetail(a, ok)} />
                      <Term label="Billed" {...billedDetail(a, money?.billedLast12 ?? null)} />
                      <Term
                        label="Used"
                        value={a.billed && c.usage.oneTrade?.current?.activeUsers30d != null ? String(c.usage.oneTrade.current.activeUsers30d) : 'Not measured for it'}
                        none={!(a.billed && c.usage.oneTrade?.current?.activeUsers30d != null)}
                        sub={a.billed && c.usage.oneTrade?.current?.licencesCount != null ? `of ${c.usage.oneTrade.current.licencesCount.toLocaleString('en-GB')} licences, last thirty days` : 'what is measured is measured for the company, not for one agreement'} />
                      <Term label="Service levels" value="Not confirmed" none sub="Nothing here has been checked against the signed paper" />
                    </div>
                    <div className="subopts">
                      {/* One yellow lead per card (brief section 6): the first opened agreement's paper is the lead, the
                          others' plain (live, 7 Oct: SBO Bet's two opened agreements carried two). */}
                      <ReadAgreement a={a} quiet={inForce.findIndex((x) => openDeals.has(x.id)) !== i} />
                      {/* "Draft a renewal note" was here and called open('/'), drafting nothing. Removed
                          with the two on the head row, for the same reason. */}
                      {/* BUGS.md D51: write the reminder halfway to notice, with Undo for a week. */}
                      <NoticeReminder a={a} now={now} openTasks={tasks} companyId={c.id} companyName={c.name} store={store}
                        onNoDate={(title) => { setAddTitle(title); setAdding(true); document.getElementById('a-head')?.scrollIntoView({ block: 'start', behavior: 'smooth' }); }} />
                    </div>
                  </>
                ) : null}
              </div>
            ))}
          </div>
        )}
        {endedAgreements.length > 0 ? (
          <>
            <div className="hx-bandline lv-ended"><h3>Ended</h3><span className="hx-meta">{endedAgreements.length === 1 ? 'ran to its end date and did not renew' : 'ran to their end dates and did not renew'}</span></div>
            <div className="rows">
              {endedAgreements.map((a) => (
                <div className={openDeals.has(a.id) ? 'row ended open' : 'row ended'} key={a.id}>
                  <ActionButton reading type="button" className="rt rt-open" aria-expanded={openDeals.has(a.id)}
                    onClick={() => setOpenDeals((was) => { const next = new Set(was); if (next.has(a.id)) next.delete(a.id); else next.add(a.id); return next; })}>
                    {a.title}
                  </ActionButton>
                  <span className="rw"><span className="clock ended">Ended {formatDay(a.endDate!)}</span></span>
                  {openDeals.has(a.id) ? (
                    <>
                      <div className="terms">
                        <Term label="Term" value={a.start ? `${formatDay(a.start)} → ${formatDay(a.endDate!)}` : `To ${formatDay(a.endDate!)}`} words sub={a.start ? undefined : 'No start date is on record for it'} />
                        {/* What it cost while it ran. Never weighed against today's billing, which an ended agreement is
                            not expected to have: "finance's own figures do not bear it out" was said of one on the mock. */}
                        <Term label="Fee" {...feeDetail(a, true)} />
                      </div>
                      <div className="subopts"><ReadAgreement a={a} quiet /></div>
                    </>
                  ) : null}
                </div>
              ))}
            </div>
          </>
        ) : null}
      </Section>

      <Elsewhere rows={c.elsewhere} go={go} />
      <BillingPanel billing={c.billing} billed={c.read ? c.read.money.billed : null} name={c.name} isAdmin={admin} open={open} />
      {money && unbilled > 0 ? (
        <Section id="a-unbilled" title="Money against the agreements" state={unbilledState(unbilled, inForce.length)}>
          <Nothing>{unbilledWords(unbilled, inForce.length)}</Nothing>
        </Section>
      ) : null}

      {/* --------------------------------------------------------- the conversations */}
      {isEmpty('a-conv') ? null : <Section id="a-conv" title="Conversations"
        state={conversationsState(written.length, calls.total, diary.length)}
        right={c.recentTotal ? `${c.recentTotal.toLocaleString('en-GB')} messages held` : undefined}>
        {conv.threads.length === 0 && calls.total === 0 && shows.length === 0 ? (
          <Nothing read={c.domains.length ? 'Read: every message and meeting against their mail domain.' : 'Cobrain finds a company by its mail domain, and this record has none.'}>
            No message and no meeting.
          </Nothing>
        ) : (
          <>
            <p className="colh" style={{ marginBottom: 6 }}>Threads</p>
            <div className="rows">
              {(allThreads ? written : written.slice(0, 5)).map((t) => {
                // REQ-2026-10-07-167: every thread opens, on the thread page (Mike by pop-up, "A thread page"), where
                // before only one whose newest message was a triage item, a meeting or a chat did (BUGS.md D166, D184).
                // A meeting or a chat still opens on its own page, which reads it better than the thread page does.
                const path = t.lastId ? conversationPath(t.lastId, t.open ?? null, store.tenantId, t.kind) : null;
                return (
                <div className="row" key={t.key}>
                  {path
                    ? <a className="rt rt-thread go" href={path} onClick={(e) => { e.preventDefault(); open(path); }}>{t.title}</a>
                    : <span className="rt rt-thread">{t.title}</span>}
                  <span className="rw">{shortDay(t.lastAt)}, {t.lastOutbound ? 'you replied last' : 'they wrote last'}</span>
                  {/* B2355: Cobrain's two lines on the thread, where it stands and what is owed. */}
                  <SummaryLines summary={t.summary} className="rs" />
                  <span className="rm">
                    <span>{plural(t.count, 'message')}{t.count > 1 ? (spanDays(t.firstAt, t.lastAt) === 0 ? ', all on one day' : ` over ${plural(spanDays(t.firstAt, t.lastAt), 'day')}`) : ''}</span>
                    {t.everyOneAReply ? <span>Every one is a reply, so it began before this</span> : null}
                    {/* The newest message itself. A thread used to be a subject, a date and "Not
                        summarised yet", which says something happened and nothing about what. */}
                    {t.lastOpens
                      ? <span className="conv-said">{t.lastWho ? <b>{cleanName(t.lastWho)}: </b> : null}&ldquo;{t.lastOpens}&rdquo;</span>
                      : <span>Cobrain holds no text for the newest message in this thread</span>}
                  </span>
                </div>
                );
              })}
            </div>
            {written.length > 5
              ? <p className="dw-addlink"><ActionButton reading type="button" className="hx-link" aria-expanded={allThreads} onClick={() => setAllThreads((v) => !v)}>{allThreads ? 'Show fewer' : `Show all ${written.length} threads`}</ActionButton></p>
              : null}
            <p className="colh" style={{ margin: '14px 0 6px' }}>Meetings</p>
            {calls.rows.length ? (
              <div className="rows">
                {calls.rows.map((m) => (
                  <div className="row" key={m.id}>
                    <a className="rt rt-thread go" href={`/meetings/${m.id}`} onClick={(e) => { e.preventDefault(); open(`/meetings/${m.id}`); }}>{m.title?.trim() || 'A call with no title'}</a>
                    <span className="rw">{shortDay(m.at)}</span>
                    <span className="rm">{m.headline ? <span className="conv-said">{m.headline}</span> : <span>Cobrain holds no summary of this call</span>}</span>
                  </div>
                ))}
              </div>
            ) : null}
            {shows.length ? (
              <div className="rows rows-shows">
                {shows.map((m) => (
                  <div className="row" key={m.id}>
                    <span className="rt rt-thread">A meeting at {m.show}</span>
                    <span className="rw">{shortDay(m.at)}</span>
                  </div>
                ))}
              </div>
            ) : null}
            {calls.total > calls.rows.length
              ? <p className="none">{(calls.total - calls.rows.length).toLocaleString('en-GB')} older {calls.total - calls.rows.length === 1 ? 'call is' : 'calls are'} on Meetings.</p>
              : null}
            <Nothing read="Read: every meeting Cobrain holds, and your diary.">
              {calls.total ? null : 'No call with them has been recorded.'}
              {diary.length
                ? <> {diary.map((t, i) => <span key={t.key}>{i ? ' ' : ''}{i === 0 && diary.length === 1 ? 'One diary entry' : 'A diary entry'}, <b>“{t.title}”</b>, on {formatDay(t.lastAt)}.</span>)}</>
                : null}
            </Nothing>
            {conv.noise || conv.total > conv.held ? (
              <p className="none" style={{ marginTop: 12 }}>
                {conv.noise ? `${conv.noise === 1 ? 'One automatic reply or acceptance was' : `${conv.noise} automatic replies and acceptances were`} left out. ` : ''}
                {conv.total > conv.held ? (c.threads
                  ? `${(conv.total - conv.held).toLocaleString('en-GB')} more ${conv.total - conv.held === 1 ? 'message is' : 'messages are'} in older threads, not listed here.`
                  : `${(conv.total - conv.held).toLocaleString('en-GB')} older messages are held and not listed here.`) : ''}
              </p>
            ) : null}
          </>
        )}
      </Section>}

      {/* REQ-2026-10-07-167: notes on the company's file, typed or added as a file, each with who added it and when. */}
      <CompanyNotes storeId={store.tenantId} companyId={c.id} companyName={c.name} open={open} />

      {/* ------------------------------------------------------------ owed each way */}
      {isEmpty('a-owed') ? null : <Section id="a-owed" title="Owed each way" state={owedState(c.owedCounts?.ours ?? ours.length, c.owedCounts?.theirs ?? theirs.length)} right={late ? `${late} past ${late === 1 ? 'its' : 'their'} date` : undefined}>
        <div className="two">
          <div>
            <p className="colh">What we owe them</p>
            <Owed rows={oursFold} now={now} empty={tasks.length ? '' : c.recentTotal
              ? `Nothing found in ${plural(c.recentTotal, 'message')} with this company.`
              : 'There is no message with this company to read.'} />
            {tasks.length ? <OwedTasks tasks={tasks} more={c.tasksMore === true} now={now} open={open} /> : null}
          </div>
          <div>
            <p className="colh">What they owe us</p>
            <Owed rows={theirsFold} now={now} empty={c.recentTotal
              ? `Nothing found in the same ${plural(c.recentTotal, 'message')}.`
              : 'There is no message with this company to read.'} />
          </div>
        </div>
        {owedTotal > c.owed.length ? <p className="note owed-listed">The {c.owed.length} most pressing are listed, of {owedTotal.toLocaleString('en-GB')} open.</p> : null}
      </Section>}

      {/* ------------------------------------------------------------------- people */}
      {isEmpty('a-people') ? null : <Section id="a-people" title="People"
        state={people.length || apart.length ? `${plural(people.length, 'person', 'people')}${apart.length ? `, ${plural(apart.length, 'shared address')} kept apart` : ''}${peopleMore ? `, of ${c.peopleTotal!.toLocaleString('en-GB')} on record` : ''}` : 'Nobody'}
        right={people.length && people.every((p) => !p.jobTitle) ? 'Nobody has a job title yet' : undefined}>
        {people.length === 0 && apart.length === 0 ? (
          <Nothing read={c.domains.length ? 'Read: every address on their mail domain.' : 'No mail domain is on this record, so no person has reached it.'}>No person and no shared address.</Nothing>
        ) : (
          <>
            {people.length ? (
              <ul className="plist">
                {/* Newest contact first, and cleanName because display_name is the ADDRESS for
                    anybody nobody has named -- the list read danny.irving@bet365.com. A person
                    Cobrain has never seen in a message says so, rather than showing a blank where
                    a date would be: that is the difference between "quiet" and "not looked". */}
                {(allPeople ? people : people.slice(0, 5)).map((p) => (
                  <li className="p" key={p.id}>
                    <span className="pn">{go(`/people/${p.id}`, cleanName(p.name))}</span>
                    {p.jobTitle ? <span className="pd">{p.jobTitle}</span> : null}
                    <span className="pw">
                      {p.lastAt
                        ? <>last heard from <b>{formatDay(p.lastAt)}</b>, {plural(p.messages, 'message')}</>
                        : <span className="none">never seen in a message Cobrain holds</span>}
                    </span>
                    {p.said ? <SaidPerson said={p.said} busy={saying} onUndo={(a) => void sayPerson(() => records.undoSayPerson(store.tenantId, a))} /> : null}
                  </li>
                ))}
              </ul>
            ) : <Nothing>No person yet — every address on this company is a desk or a mailbox.</Nothing>}
            {people.length > 5
              ? <p className="dw-addlink"><ActionButton reading type="button" className="hx-link" aria-expanded={allPeople} onClick={() => setAllPeople((v) => !v)}>{allPeople ? 'Show fewer' : `Show all ${people.length}`}</ActionButton></p>
              : null}
            {/* BUGS.md D125: the record reads the 200 heard from last, and says so where the company has more. */}
            {peopleMore ? <p className="note people-listed">The {c.people.length.toLocaleString('en-GB')} heard from last are listed, of {c.peopleTotal!.toLocaleString('en-GB')} on record.</p> : null}
            <KeptApart apart={apart} mayEdit={c.mayEdit} busy={saying} onSay={(id) => void sayPerson(() => records.sayPerson(store.tenantId, id))} />
            {sayWrong ? <p className="wrong" role="alert">{sayWrong}</p> : null}
            {/* BUGS.md D188: who handles them is said once, in the head's facts, where the head has the read. */}
            {c.read ? null : (
              <p className="none" style={{ marginTop: 12 }}>
                <b>On our side:</b> {owner ? `${owner.name ?? 'Somebody'} handles them.` : 'Nobody handles them.'}
                <span className="read">Read: the mailboxes connected to Cobrain.</span>
              </p>
            )}
          </>
        )}
      </Section>}

      {/* ------------------------------------------------------------------- rights */}
      {/* BUGS.md D87: the Rights panel came off the record on 21 Sep (14e99d75) while the API kept reading the properties
          the company holds, offers or has a deal on. Mike, by pop-up on 6 Oct: "Put it back (Recommended)", the record
          listing them as it did before 21 Sep, each opening its property; what rights options are for waits for the
          Rights talk. */}
      {isEmpty('a-rights') ? null : <Section id="a-rights" title="Rights" state={c.rights.length ? plural(c.rights.length, 'property', 'properties') : 'None on record'}>
        {c.rights.length === 0
          ? <Nothing>No property this company holds, offers or has a deal on is on record.</Nothing>
          : <p className="gist rights-list">{c.rights.map((r, i) => <span key={r.id}>{i ? <br /> : null}{go(`/rights/${r.id}`, r.propertyName)}, {r.status}</span>)}</p>}
      </Section>}



      {/* ------------------------------------ operations, then press and intelligence */}
      {isEmpty('a-ops') ? null : <Operations ops={c.operations} inForce={inForce.length} />}

      {isEmpty('a-press') ? null : <Press press={c.press} storeId={store.tenantId} openPath={open} />}

      {empty.length ? <p className="none empties" id="a-nothing">Nothing yet on {orList(empty.map((e) => e.word))}.</p> : null}

      {/* ----------------------------------------------- the record and its history */}
      <Section id="a-record" title="The record" state="and everything done to it"
        right={c.mayEdit && c.sheetFields
          ? <ActionButton type="button" className="opt sr-act" aria-expanded={changing} onClick={() => { setChanging((v) => !v); setSaid(null); setError(null); }}>{changing ? 'Done' : 'Change this company'}</ActionButton>
          : undefined}>
        <div className="fields">
          {/* BUGS.md D188: the name, kind, tiers, market, domain, who handles them and the group are the head's facts, said
              there once; the owners sheet's brand count is its own figure and says whose it is. */}
          <Field label="How they take it" value={routeWord(fields.integrationRoute.value)} />
          <Field label="Reach" value={fields.reach.value} />
          <Field label="Other names" value={c.aliases.join(', ') || null} empty="None recorded" />
          <Field label="Brands, by the owners sheet" value={fields.brandCount.value === null ? null : String(fields.brandCount.value)} />
          <Field label="Potential a year" value={fields.potentialRevenueGbp.value === null ? null : `£${fields.potentialRevenueGbp.value.toLocaleString('en-GB')}`} />
          <Field label="On record since" value={formatDay(c.createdAt)} />
        </div>

        {c.proposal && !answered ? (
          <div className="prop">
            <span><b>Cobrain proposes {c.proposal.name ?? 'somebody else'}</b> handles them, on {plural(c.proposal.count, 'message')} with them in {plural(c.proposal.months, 'month')}{c.proposal.ownerCount > 0 ? `, against ${plural(c.proposal.ownerCount, 'message')} from ${owner?.name ?? 'the owner'}` : owner ? `, where ${owner.name ?? 'the owner'} has none` : ', and nobody handles them now'}.</span>
            {c.mayEdit && c.sheetFields && c.proposal.judgementId ? (
              <>
                <ActionButton type="button" className="opt" disabled={busy} onClick={() => void answer('accept')}>Accept</ActionButton>
                <ActionButton type="button" className="opt" disabled={busy} onClick={() => void answer('keep')}>{owner ? `Keep ${owner.name?.split(' ')[0] ?? 'the owner'}` : 'Leave it with nobody'}</ActionButton>
              </>
            ) : null}
          </div>
        ) : null}
        {answered ? <p className="said">{answered === 'accept' ? 'Accepted. The record now says so, with you as the reason.' : 'Kept. Your answer is on the record beside the proposal.'}</p> : null}

        {changing ? (
          <div className="edit">
            <EditRow label="Account tier"><Text value={fields.accountTier.value ?? ''} busy={busy} onSave={(v) => void save('account_tier', v || null)} /></EditRow>
            <EditRow label="Home market"><Two a={fields.hq.value.market ?? ''} b={fields.hq.value.iso ?? ''} busy={busy} onSave={(a, b) => void save('hq_market', a || null, async () => { const r = await records.editCompany(store.tenantId, c.id, 'hq_iso', b || null); setFields(r); })} /></EditRow>
            <EditRow label="How they take it"><Pick value={fields.integrationRoute.value ?? ''} options={ROUTES} busy={busy} onSave={(v) => void save('integration_route', v || null)} /></EditRow>
            <EditRow label="What they are to us"><Pick value={fields.accountType.value ?? ''} options={[{ v: 'client', label: 'Client' }, { v: 'prospect', label: 'Prospect' }]} busy={busy} onSave={(v) => void save('account_type', v || null)} /></EditRow>
            <EditRow label="Who handles them">
              <Pick value={owner?.principalId ?? ''} options={c.team.map((t) => ({ v: t.principalId, label: t.name }))} busy={busy} onSave={(v) => void save('owner_principal_id', v || null)} />
            </EditRow>
          </div>
        ) : null}
        {said ? <p className="said">{said}</p> : null}
        {error ? <p className="wrong">{error}</p> : null}

        <ul className="hist">
          {c.history.length === 0
            ? <li>Nothing has been done to this record since it was created. <span className="hw">{formatDay(c.createdAt)}</span></li>
            : historyRows(c.history).map((r, i) => (
              <li key={i}><span className="hl">{r.line}{r.times > 1 ? ` (${r.times === 2 ? 'twice' : `${r.times} times`})` : ''}</span><span className="hw">{r.day}</span></li>
            ))}
        </ul>
      </Section>
    </div>
  );
}

/* --------------------------------------------------------------- small parts */

function Term({ label, value, sub, none, words }: { label: string; value: string; sub?: string | undefined; none?: boolean; words?: boolean }) {
  return (
    <div className="term">
      <span className="tl">{label}</span>
      <span className={none ? 'tv no' : words ? 'tv words' : 'tv'}>{value}</span>
      {sub ? <span className="ts">{sub}</span> : null}
    </div>
  );
}

function Field({ label, value, empty = 'Not recorded' }: { label: string; value: string | null | undefined; empty?: string }) {
  return (
    <div className="fd">
      <span className="fdl">{label}</span>
      <span className={value ? 'fdv' : 'fdv no'}>{value || empty}</span>
    </div>
  );
}

function EditRow({ label, children }: { label: string; children: ReactNode }) {
  return <div className="erow"><span className="ek">{label}</span><span className="ev">{children}</span></div>;
}

function Text({ value, busy, onSave }: { value: string; busy: boolean; onSave: (v: string) => void }) {
  const [v, setV] = useState(value);
  return (
    <>
      <label className="field"><ActionInput aria-label="Value" value={v} onChange={(e) => setV(e.target.value)} /></label>
      <ActionButton type="button" className="opt" disabled={busy || v === value} onClick={() => onSave(v)}>Save</ActionButton>
    </>
  );
}

function Two({ a, b, busy, onSave }: { a: string; b: string; busy: boolean; onSave: (a: string, b: string) => void }) {
  const [x, setX] = useState(a); const [y, setY] = useState(b);
  return (
    <>
      <label className="field"><ActionInput aria-label="Home market" placeholder="market" value={x} onChange={(e) => setX(e.target.value)} /></label>
      <label className="field" style={{ width: 84 }}><ActionInput aria-label="Country code" placeholder="code" maxLength={2} value={y} onChange={(e) => setY(e.target.value)} /></label>
      <ActionButton type="button" className="opt" disabled={busy || (x === a && y === b)} onClick={() => onSave(x, y)}>Save</ActionButton>
    </>
  );
}

function Pick({ value, options, busy, onSave }: { value: string; options: Array<{ v: string; label: string }>; busy: boolean; onSave: (v: string) => void }) {
  const [v, setV] = useState(value);
  return (
    <>
      <label className="field"><ActionSelect aria-label="Value" value={v} onChange={(e) => setV(e.target.value)}>
        <option value="">not set</option>
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </ActionSelect></label>
      <ActionButton type="button" className="opt" disabled={busy || v === value} onClick={() => onSave(v)}>Save</ActionButton>
    </>
  );
}

/**
 * The signed agreement, where there is one to read. This button was `disabled` and did nothing at
 * all, on a register where 115 of 150 rows carry the document Cobrain ingested and 136 carry a link.
 * Two routes: the ingested document opens on its own item page, and failing that a link opens where
 * it points -- but only if it IS a link. The register's `contract_link` is free text and often holds
 * a reference like "1003 - bet365 (MODUS)", which is not somewhere a browser can go, so it is shown
 * as the reference it is rather than dressed up as a button that would fail.
 */
function ReadAgreement({ a, quiet = false }: { a: Agreement; quiet?: boolean }) {
  // THE SIGNED PAPER, not the register's note about it. This read `contractLink`, which is the
  // register's OWN TEXT: of 116 live rows two hold a URL and a hundred hold a phrase like
  // "4009 - Modus Online", so the button worked on two rows in the whole store. Meanwhile 89 of
  // those rows carry a document from the SharePoint Contracts tree, and all 87 distinct documents
  // behind them have a real web address, a filename and a size. The paper was there the whole
  // time and this was reading the wrong column.
  const doc = a.document;
  const docUrl = doc?.url && /^https?:\/\//i.test(doc.url) ? doc.url : null;
  if (docUrl) {
    return (
      <span className="agree-open">
        {/* An ended agreement's paper is history, not the next thing to do, so it is not the yellow lead (U325). */}
        <a className={quiet ? 'opt' : 'opt lead'} href={docUrl} target="_blank" rel="noreferrer">
          {/* The paper in words, never its file name (U324): this printed "iGame-bet365 MSS License
              Agreement_FINAL.docx.pdf" on the yellow lead itself. */}
          <span className="l">Open the signed agreement</span> <small>{paperLine(doc!)}</small>
        </a>
        {/* One piece of paper behind two rows is worth saying: Bet365's two Content Licencing rows
            share a document, and a reader offered the same button on both should know that. */}
        {doc!.sharedWith > 0
          ? <small className="agree-note">the same file is filed against {doc!.sharedWith === 1 ? 'one other agreement' : `${countWord(doc!.sharedWith).toLowerCase()} other agreements`} here</small>
          : null}
        {/* BUGS.md D168: MODUS Online's Rights Holder Services row is joined on live to "iGame-Entain MSS License
            Agreement 2025.docx"; the button still opens what the register joined, and says whose name the file bears. */}
        {doc!.namesOther?.length
          ? <small className="agree-note agree-other">the file is named for {doc!.namesOther.join(' and ')}, not this company</small>
          : null}
      </span>
    );
  }
  // A register link that IS a URL, where no document was taken from SharePoint.
  const link = a.contractLink && /^https?:\/\//i.test(a.contractLink.trim()) ? a.contractLink.trim() : null;
  if (link) {
    return <a className={quiet ? 'opt' : 'opt lead'} href={link} target="_blank" rel="noreferrer"><span className="l">Open the signed agreement</span> <small>where the register files it</small></a>;
  }
  if (doc) {
    return <span className="opt none"><span className="l">Cobrain holds {paperWords(doc.name)}</span> <small>no address to open it at</small></span>;
  }
  if (a.contractLink) {
    return <span className="opt none"><span className="l">Not filed where Cobrain can open it</span> <small>the register calls it &ldquo;{a.contractLink}&rdquo;</small></span>;
  }
  return <span className="opt none"><span className="l">No signed agreement on file</span> <small>nothing is attached to this register row</small></span>;
}

/**
 * What the company is worth, said honestly. The register and finance's copy disagree for most
 * companies -- 22 of 92 agree within a quarter -- so the page states both and names the direction
 * of the disagreement instead of picking one and calling it the answer. Saying "worth £5,916,000 a
 * year" over a client finance booked £11,500 for is not a rounding error, it is a wrong statement
 * about the business.
 */
function Worth({ r }: { r: Reconciliation }) {
  const gbp = (n: number) => `£${Math.round(n).toLocaleString('en-GB')}`;
  // Where only some agreements' own lines were weighed, the sentence names them and gives the company's booked
  // total apart: the compared sum is never the company's money (BUGS.md D159).
  if (r.kind === 'agree' || r.kind === 'register-higher' || r.kind === 'register-lower') {
    const words = comparedWords(r);
    if (words) return <>{words}</>;
  }
  switch (r.kind) {
    case 'agree':
      return <>The register and finance agree: <b>{gbp(r.booked)}</b> booked{r.months < 12 ? <> over {plural(r.months, 'month')}</> : ' over twelve months'}.</>;
    case 'register-higher':
      // It used to end "so one of the two is wrong and the page will not choose". Mike, on reading
      // it: "Bet365, yes the revenue is wrong, £5,916,000 a year is 100% not correct ... you need
      // to map everything to the billing" (A37). G6 already said Anaplan is the only source of
      // truth, and refusing to choose between a source of truth and a spreadsheet was not
      // even-handedness, it was the page declining to do its job.
      return <>Finance booked <b>{gbp(r.booked)}</b> over {r.months < 12 ? plural(r.months, 'month') : 'twelve months'}, and that is the money. Month for month the register claims <b>{timesWords(r.times)}</b> as much, so its fees are not to be trusted on this company.</>;
    case 'register-lower':
      return <>Finance booked <b>{gbp(r.booked)}</b> over {r.months < 12 ? plural(r.months, 'month') : 'twelve months'}, and that is the money. Month for month the register accounts for only <b>{Math.round(r.times * 100)}%</b> of it.</>;
    case 'nothing-booked':
      return <>Finance has booked nothing against them in twelve months. The register claims <b>{gbp(r.contracted)}</b> a year and nothing confirms it.</>;
    case 'no-figure':
      return r.booked ? <>Finance booked <b>{gbp(r.booked)}</b> over twelve months. No comparable figure on the register.</> : <>Neither the register nor finance carries a figure for them.</>;
  }
}

/**
 * Whether finance's own figures bear the register out: true where they agree, false only where they differ, and
 * 'nothing' where finance holds no figure to compare (nothing booked, or no figure on either side), which is no
 * contradiction (BUGS.md D167).
 */
function corroborated(r: Reconciliation): Bearing {
  return r.kind === 'agree' ? true : r.kind === 'register-higher' || r.kind === 'register-lower' ? false : 'nothing';
}

/**
 * A task written against THIS company, in place. The button used to navigate to the task list,
 * which lost the company and made the reader start again -- Mike: "it stupidly takes it to the task
 * list, but the company register!".
 */
export function AddTask({ companyId, companyName, store, onDone, title: named = '', deals = [] }: {
  companyId: string; companyName: string; store: PageProps['store']; onDone: (said: string) => void;
  /** BUGS.md D51: the agreement an unset reminder is for, already in the box. */
  title?: string;
  /** BUGS.md D89, D90: the company's open deals. With two or more the form asks which the task is a step of, or none. */
  deals?: Array<{ id: string; title: string }>;
}) {
  // A task needs a title, a date and a priority, and the schema refuses one without them, so the
  // form asks for all three rather than inventing two of them behind the reader. The date defaults
  // to a week out because that is the commonest answer, not because it is the right one.
  const inAWeek = new Date(Date.now() + 7 * 86400_000).toISOString().slice(0, 10);
  const [title, setTitle] = useState(named);
  const [due, setDue] = useState(inAWeek);
  const [priority, setPriority] = useState(3);
  // Mike by pop-up, 7 Oct about 12:00 CEST, "On its deal when there is one (Recommended)": "With two or more, the form
  // asks which deal, or none." With one, the API puts the task on it; with none, it stays the company's.
  const ask = deals.length >= 2;
  const [deal, setDeal] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [wrong, setWrong] = useState<string | null>(null);
  // Held from the first Enter until the save answers (BUGS.md TK37): Enter calls save() as often as it is pressed, and a
  // busy state alone is read before React re-renders, so three Enters saved one task three times on live.
  const lock = useRef(false);
  const ready = Boolean(title.trim()) && (!ask || deal !== '');
  async function save() {
    if (lock.current || !ready) return;
    lock.current = true;
    setBusy(true); setWrong(null);
    try {
      // `dealId` names the deal, null says none; left out with fewer than two, the API decides as above.
      const body = { title: title.trim(), dueAt: due, priority, organisationId: companyId, ...(ask ? { dealId: deal === 'none' ? null : deal } : {}) };
      await tasks.add(store.tenantId, body);
      onDone(`Task added against ${companyName}: \u201c${title.trim()}\u201d, due ${formatDay(due)}.`);
    } catch (e) {
      setWrong(e instanceof ApiError ? e.message : 'The task was not saved.');
    } finally { lock.current = false; setBusy(false); }
  }
  return (
    <div className="addtask">
      <label>What needs doing?
        <ActionInput type="text" value={title} autoFocus disabled={busy} onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void save(); }} placeholder={`Something for ${companyName}`} />
      </label>
      <label>By when
        <ActionInput type="date" value={due} disabled={busy} onChange={(e) => setDue(e.target.value)} />
      </label>
      <label>How urgent
        <ActionSelect value={priority} onChange={(e) => setPriority(Number(e.target.value))}>
          <option value={1}>Highest</option><option value={2}>High</option><option value={3}>Normal</option><option value={4}>Low</option>
        </ActionSelect>
      </label>
      {ask ? (
        <label>Which deal?
          <ActionSelect value={deal} disabled={busy} onChange={(e) => setDeal(e.target.value)}>
            <option value="" disabled>Choose</option>
            {deals.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            <option value="none">None</option>
          </ActionSelect>
        </label>
      ) : null}
      <ActionButton type="button" className="opt" disabled={busy || !ready} onClick={() => void save()}>{busy ? 'Saving\u2026' : 'Add it'}</ActionButton>
      {busy ? <p className="note" role="status">Saving this task.</p> : null}
      {wrong ? <p className="awrong" role="alert">{wrong}</p> : null}
    </div>
  );
}

/**
 * BUGS.md D51: an agreement card's "Put a reminder before the notice date" opened Tasks and wrote nothing.
 * The notice day stays in the task title; its due day is halfway from today, rounded down.
 * Undo lasts 7 days from when the task was made, read back from the record's open tasks with its saved due day.
 * With no notice day still to come, keep opening Add task with the agreement named.
 */
export function NoticeReminder({ a, now, openTasks = [], companyId, store, onNoDate }: {
  a: Pick<Agreement, 'title' | 'noticeDays' | 'endDate'>; now: Date; companyId: string; companyName: string; store: PageProps['store']; onNoDate: (title: string) => void;
  openTasks?: readonly CompanyTask[];
}) {
  const by = noticeBy(a, now);
  const week = 7 * 86_400_000;
  const [reminder, setReminder] = useState<{ id: string; due: string; createdAt: number } | null>(() => {
    if (!by) return null;
    const title = `${a.title}: notice by ${plateDay(by)}`;
    const task = openTasks.find((t) => {
      const age = now.getTime() - Date.parse(t.createdAt ?? '');
      return t.title === title && t.dueAt !== null && age >= 0 && age < week;
    });
    return task ? { id: task.id, due: task.dueAt!, createdAt: Date.parse(task.createdAt!) } : null;
  });
  const remaining = reminder ? reminder.createdAt + week - now.getTime() : 0;
  useEffect(() => {
    if (!reminder) return;
    const timer = setTimeout(() => setReminder(null), Math.max(0, remaining));
    return () => clearTimeout(timer);
  }, [reminder, remaining]);
  const [takenBack, setTakenBack] = useState(false);
  const [wrong, setWrong] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  async function put() {
    if (!by) { onNoDate(a.title); return; }
    if (lock.current) return;
    lock.current = true;
    setBusy(true); setWrong(null);
    const title = `${a.title}: notice by ${plateDay(by)}`;
    const today = Date.parse(`${iso(now)}T00:00:00Z`);
    const days = Math.round((Date.parse(`${by}T00:00:00Z`) - today) / 86_400_000);
    const due = days <= 1 ? by : iso(new Date(today + Math.floor(days / 2) * 86_400_000));
    try {
      const body = { title, dueAt: due, priority: 3, organisationId: companyId, dealId: null };
      const { id } = await tasks.add(store.tenantId, body);
      setReminder({ id, due, createdAt: now.getTime() });
      setTakenBack(false);
    } catch (e) {
      setWrong(e instanceof ApiError ? e.message : 'The task was not saved.');
    } finally { lock.current = false; setBusy(false); }
  }
  async function undo() {
    if (!reminder || lock.current) return;
    lock.current = true;
    setBusy(true); setWrong(null);
    try {
      await tasks.act(store.tenantId, reminder.id, 'not_a_task');
      setReminder(null);
      setTakenBack(true);
    } catch (e) {
      setWrong(e instanceof Error ? e.message : String(e));
    } finally { lock.current = false; setBusy(false); }
  }
  return (
    <>
      {reminder ? <p className="asaid" role="status">Reminder set for {formatDay(reminder.due)}.{' '}<OptionButton type="button" disabled={busy} onClick={() => void undo()}>Undo</OptionButton></p> : <>
        {takenBack ? <p className="asaid" role="status">Reminder taken back.</p> : null}
        <OptionButton type="button" disabled={busy} onClick={() => void put()}>Put a reminder before the notice date</OptionButton>
      </>}
      {wrong ? <p className="awrong" role="alert">{wrong}</p> : null}
    </>
  );
}

/** Today as YYYY-MM-DD, for comparing against a due date that is a day and not an instant. */
function iso(d: Date): string { return d.toISOString().slice(0, 10); }

/** The heading's state: what each side is carrying, in words, never "Nothing found" when something was found. */
function owedState(ours: number, theirs: number): string {
  if (ours === 0 && theirs === 0) return 'Nothing found';
  const side = (n: number, who: string) => (n === 0 ? `nothing ${who}` : `${countWord(n).toLowerCase()} ${who}`);
  return capital(`${side(ours, 'we owe')}, ${side(theirs, 'they owe')}`);
}

/** A line's first word in capitals, so no head starts in lower case (BUGS.md D179). */
function capital(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }

/** "Money against the agreements", with the singular and the one-agreement case said as they read (BUGS.md D179). */
function unbilledState(unbilled: number, inForce: number): string {
  if (inForce === 1) return 'The one agreement in force carries no billing line';
  return `${countWord(unbilled)} of ${countWord(inForce).toLowerCase()} ${unbilled === 1 ? 'carries' : 'carry'} no billing line`;
}
function unbilledWords(unbilled: number, inForce: number): string {
  const one = unbilled === 1;
  const head = inForce === 1
    ? 'The one agreement in force has no money against it.'
    : `${countWord(unbilled)} of the ${countWord(inForce).toLowerCase()} agreements in force ${one ? 'has' : 'have'} no money against ${one ? 'it' : 'them'}.`;
  return `${head} Either ${one ? 'it is' : 'they are'} billed under a name Cobrain has not joined to this company, or ${one ? 'it is' : 'they are'} not billed at all. This is worth putting to finance.`;
}

/**
 * What is owed, one promise a row. The statement is Cobrain's own extraction and is shown verbatim
 * rather than summarised, because a promise reworded is a different promise. A date it never found
 * is said to be missing; a date that has passed is marked, since an overdue promise is the whole
 * reason to look at this section.
 */
function Owed({ rows: { shown, folded }, now, empty }: { rows: { shown: CompanyRecordView['owed']; folded: CompanyRecordView['owed'] }; now: Date; empty: string }) {
  const [older, setOlder] = useState(false);
  if (shown.length === 0 && folded.length === 0) return empty ? <Nothing>{empty}</Nothing> : null;
  const today = iso(now);
  const rows = older ? [...shown, ...folded] : shown;
  return (
    <>
    {rows.length > 0 ? <ul className="owed">
      {rows.map((w) => {
        const late = w.dueAt !== null && w.dueAt < today;
        return (
          <li key={w.id} className={late ? 'owed-row late' : 'owed-row'}>
            <span className="owed-what">{w.statement}</span>
            <span className="owed-when">
              {/* cleanName, because crm.person.display_name is the ADDRESS for a contact nobody has
                  named, and the live page read "Mike Cobain promised Mike.Thomas@bet365.com". */}
              {w.byName ? <><b>{cleanName(w.byName)}</b>{w.toName ? <> promised {cleanName(w.toName)}</> : null}, </> : null}
              {w.dueAt ? <>{late ? 'was due ' : 'due '}<b>{formatDay(w.dueAt)}</b></> : 'no date on it'}
              {/* U368 (Mike by pop-up, 7 Oct 09:33): a promise read in mail and not accepted is "not on record yet". */}
              {w.status === 'suggested' ? ', not on record yet' : ', on record'}
            </span>
            {/* The words it was drawn from. Without them the promise is an assertion a reader has
                no way to check; with them they can see Cobrain's working and judge it. */}
            {w.quote
              ? <span className="owed-quote">&ldquo;{w.quote}&rdquo;{w.quoteAt ? <em> — {w.quoteSubject ? `${w.quoteSubject}, ` : ''}{formatDay(w.quoteAt)}</em> : null}</span>
              : <span className="owed-quote none">Cobrain kept no quote for this one</span>}
          </li>
        );
      })}
    </ul> : null}
    {/* The fold, in Tasks' own words (TasksPage, "older, more than a month overdue"): kept, never removed. */}
    {/* BUGS.md D191: a toggle, so what it opened can be closed again. */}
    {folded.length > 0
      ? <ActionButton reading type="button" className="hx-link owed-older" aria-expanded={older} onClick={() => setOlder((v) => !v)}>{older ? `Hide the ${folded.length} older` : `${folded.length} older, more than a month overdue and never accepted. Show`}</ActionButton>
      : null}
    </>
  );
}

/**
 * The open tasks filed on the company (BUGS.md D89), under What we owe them, each marked as a task and opening on Tasks.
 * Mike by pop-up, 8 Oct about 12:37 CEST: "show it under 'Owed each way - What we owe them', marked as a task, open ones only".
 */
function OwedTasks({ tasks, more, now, open }: { tasks: NonNullable<CompanyRecordView['tasks']>; more: boolean; now: Date; open: (path: string) => void }) {
  const today = iso(now);
  return (
    <>
      <ul className="owed owed-tasks">
        {tasks.map((t) => {
          const late = t.dueAt !== null && t.dueAt < today;
          const path = `/tasks/${encodeURIComponent(t.id)}`;
          return (
            <li key={t.id} className={late ? 'owed-row owed-task late' : 'owed-row owed-task'}>
              <span className="owed-what"><span className="owed-kind">Task</span> <a href={path} onClick={(e) => { e.preventDefault(); open(path); }}>{t.title}</a></span>
              <span className="owed-when">
                {t.ownerName ? <><b>{cleanName(t.ownerName)}</b>, </> : null}
                {t.dueAt ? <>{late ? 'was due ' : 'due '}<b>{formatDay(t.dueAt)}</b></> : 'no date on it'}
                {t.dealTitle ? `, on ${t.dealTitle}` : ''}
              </span>
            </li>
          );
        })}
      </ul>
      {more ? <p className="note">The {tasks.length} soonest open tasks are listed; the rest are on Tasks.</p> : null}
    </>
  );
}

/** The contracted value as a clause inside the head sentence, or nothing where no row carries a figure. */
function contractedSentence(rows: Agreement[]): string {
  const { totals, unpriced } = contractedAYear(rows);
  if (totals.length === 0) return '';
  const tail = unpriced.length ? ` (${unpriced.length} of them carrying no figure)` : '';
  return `, worth ${totalLine(totals)} a year on the register${tail}`;
}


/** "a, b and c", capitalised, so the brief's foot reads as a sentence and not as a list. */
export function sentence(parts: string[]): string {
  const joined = parts.length <= 1 ? (parts[0] ?? '')
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
  return joined.charAt(0).toUpperCase() + joined.slice(1);
}

function shortDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getUTCDate()} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getUTCMonth()]}`;
}

function routeWord(v: string | null): string | null {
  return v === 'igm_live' ? 'IGM Live' : v === 'genius' ? 'Genius' : v === 'both' ? 'Both routes' : v === 'integrating' ? 'Integrating' : null;
}

/** The history names a field the way the page names it, never the way the column is spelt. */
function fieldWord(f: string): string {
  const words: Record<string, string> = {
    account_tier: 'account tier', account_type: 'what they are to us', brand_count: 'brands',
    hq_iso: 'home market', hq_market: 'home market', integration_route: 'how they take it',
    owner_name_sheet: 'who handles them', owner_principal_id: 'who handles them', reach: 'reach',
    potential_revenue_gbp: 'potential a year', supplies_us: 'whether they supply us',
  };
  return words[f] ?? f.replace(/_/g, ' ');
}

/** Who did it, in words, where no person is named (BUGS.md D180): never "by seed" or "by user". */
const ACTOR_WORDS: Record<string, string> = {
  sheet: ' from a spreadsheet', seed: ' when the record was first loaded', user: ' by a person', cobrain: ' by Cobrain',
  repair: ' in a repair', import: ' in an import', pipeline: ' by Cobrain', system: ' by Cobrain',
};

/** The History list's rows (BUGS.md D180): the same line on the same day said once, with how many times. */
export function historyRows(history: Array<{ at: string; who: string | null; actor: string; fields: string[] }>): Array<{ line: string; day: string; times: number }> {
  const out: Array<{ line: string; day: string; times: number }> = [];
  for (const h of history) {
    const line = historyLine(h);
    const day = formatDay(h.at);
    const same = out.find((r) => r.line === line && r.day === day);
    if (same) same.times += 1; else out.push({ line, day, times: 1 });
  }
  return out;
}

/**
 * A history line counts what a reader can count. Two columns can name one field on the page —
 * a market and its country code are one "home market" — so the words are folded before they are
 * counted, and "nine fields" never appears above a list of seven.
 */
export function historyLine(h: { who: string | null; actor: string; fields: string[] }): string {
  const named = [...new Set(h.fields.map(fieldWord))];
  const by = h.who ? ` by ${h.who}` : ACTOR_WORDS[h.actor] ?? ' by Cobrain';
  if (named.length === 0) return `Opened${by}.`;
  return `${plural(named.length, 'field')} set${by} — ${named.join(', ')}.`;
}

/**
 * Operations, from the company's OWN incident log (module 14 W24; module 07 S13.3).
 *
 * What stood here said "Open incidents: None — nothing has been raised against this client", in
 * those words, on every company page, while the log held 3,615 rows and 23 of them named Bet365.
 * Mike's word for that kind of section is dead data, and he is right: a figure that reads the same
 * whether or not anything happened is not an instrument.
 *
 * So the section separates three states it could never tell apart before: nothing has happened,
 * nothing has been COPIED, and the log does not reach this client's name at all. The last one
 * matters because the client column is free text: a platform-wide label is never attributed to a
 * client, so a company whose incidents were all filed as "All Infront" reads as having none here,
 * and the page says which labels it did match rather than leaving the reader to guess.
 */
function Operations({ ops, inForce }: { ops: CompanyRecordView['operations']; inForce: number }) {
  // BUGS.md D165: the read lists those never closed first, then the newest; five show, and "Show all" the rest.
  const [all, setAll] = useState(false);
  const clock = inForce
    ? `None of the service-level terms in their agreements has been confirmed, so no response time is measured`
    : 'No agreement in force, so no response time is measured';
  if (!ops) {
    return (
      <Section id="a-ops" title="Operations" state="No log here yet">
        <Nothing read="Read: the company's own incident log.">The incident log has not been copied into this store, so nothing can be said about how this client has been served. {clock}.</Nothing>
      </Section>
    );
  }
  const runsTo = ops.logTo ? `The log runs to ${formatDay(ops.logTo)}.` : 'The log is empty: nothing has been copied yet.';
  if (ops.recent.length === 0) {
    return (
      <Section id="a-ops" title="Operations" state="Nothing against their name">
        <Nothing read="Read: the company's own incident log, matched on their name and aliases. A platform-wide label is never counted against one client.">
          No incident in the log names this client. {runsTo} {clock}.
        </Nothing>
      </Section>
    );
  }
  return (
    <Section id="a-ops" title="Operations"
      state={`${countWord(ops.last12)} in the last year`}
      right={ops.unresolved ? `${ops.unresolved} left unresolved` : undefined}>
      <ul className="ops">
        {(all ? ops.recent : ops.recent.slice(0, 5)).map((r, i) => <IncidentRow key={`${r.at}-${i}`} r={r} />)}
      </ul>
      {ops.recent.length > 5
        ? <p className="dw-addlink"><ActionButton reading type="button" className="hx-link" aria-expanded={all} onClick={() => setAll((v) => !v)}>{all ? 'Show fewer' : `Show all ${ops.recent.length}`}</ActionButton></p>
        : null}
      <p className="none">
        {runsTo} {clock}.
        <span className="read">Read: the company&rsquo;s own incident log, under {ops.labels.length === 1 ? 'the label' : 'the labels'} {ops.labels.map((l) => `“${l}”`).join(', ')}.</span>
      </p>
    </Section>
  );
}

/** One incident (BUGS.md D173): "Basketball, Gloria Cup: booking error on ONE Platform", "Severity 3, 4 minutes", and the
 *  note split on its bullets into a list, two lines showing and "Show the rest". */
function IncidentRow({ r }: { r: NonNullable<CompanyRecordView['operations']>['recent'][number] }) {
  const [more, setMore] = useState(false);
  const lines = incidentNote(r.detail);
  return (
    <li className={r.unresolved ? 'ops-row open' : 'ops-row'}>
      <span className="ops-when">{formatDay(r.at)}</span>
      <span className="ops-what">{incidentHead(r)}{r.unresolved ? ', never resolved' : ''}</span>
      {r.severity || r.minutes !== null
        ? <span className="ops-detail">{[severityWords(r.severity), r.minutes !== null ? plural(r.minutes, 'minute') : null].filter(Boolean).join(', ')}</span>
        : null}
      {lines.length ? (
        <ul className="ops-note">
          {(more ? lines : lines.slice(0, 2)).map((l, j) => <li key={j}>{l}</li>)}
        </ul>
      ) : null}
      {lines.length > 2 ? <ActionButton reading type="button" className="hx-link" aria-expanded={more} onClick={() => setMore((v) => !v)}>{more ? 'Show less' : 'Show the rest'}</ActionButton> : null}
    </li>
  );
}

/** "Basketball, Gloria Cup: booking error on ONE Platform": where, then what, then on what (BUGS.md D173). */
export function incidentHead(r: { sport: string | null; competition: string | null; category: string | null; product: string | null }): string {
  const where = [r.sport, r.competition].filter((x): x is string => Boolean(x && x.trim())).join(', ');
  // Lower case after the colon, but an acronym keeps its capitals ("VOD missing", not "vOD missing").
  const lower = (c: string) => (/^[A-Z][a-z]/.test(c) ? c.charAt(0).toLowerCase() + c.slice(1) : c);
  const what = r.category ? (where ? lower(r.category) : r.category) : '';
  const head = where && what ? `${where}: ${what}` : where || what || 'An incident';
  return r.product ? `${head} on ${r.product}` : head;
}

/** A severity the log writes as a code, "S3", in words, "Severity 3"; a word ("High") as it stands (BUGS.md D173). */
export function severityWords(s: string | null): string | null {
  if (!s) return null;
  const m = /^s(\d)$/i.exec(s.trim());
  return m ? `Severity ${m[1]}` : s.trim();
}

/** The log's note as lines: split on its bullets and line breaks, each line trimmed, the empty ones dropped. */
export function incidentNote(detail: string | null): string[] {
  if (!detail) return [];
  return detail.split(/\u2022|\n/).map((x) => x.trim()).filter(Boolean);
}

/**
 * Press and intelligence (module 06; N19).
 *
 * What stood here said "Nothing about them — no trade-press article Cobrain has read names this
 * company", in those words, for every company, while the store held 12,795 matches over 253
 * companies and 223 of them were Bet365's, the newest drawn the same day the page was read.
 *
 * N19 is why an article that names them and a consequence somebody drew are counted apart: a
 * rule-written implication restates why the match happened and says nothing about what it means,
 * so it is never dressed up as a reading. Where nothing has been drawn the section says exactly
 * that, which is a finding rather than a blank.
 */
/** The newest press and intelligence items shown before "Show all" (BUGS.md D136: the read took five and had no way on). */
export const PRESS_FIRST = 5;

/**
 * BUGS.md D42 (Mike by pop-up, 8 Oct 2026, "All as recommended (Recommended)": "a short 'Read' list under Press, the
 * newest five lines, each opening its item"): the newest Company intelligence lines the reader's read-outs hold on this
 * company, under the press, in the band line the record's other lists wear. A line is its own words and its day; the
 * words open the message it was written from, where the record's conversations open one. A record that holds lines and
 * no press shows the lines alone, so it never says nothing Cobrain has read names a company Cobrain has written about.
 */
function ReadLines({ read, storeId, openPath }: { read: NonNullable<CompanyRecordView['press']['read']>; storeId: string; openPath: (path: string) => void }) {
  if (read.length === 0) return null;
  return (
    <div className="press-read">
      <div className="hx-bandline"><h3>Read</h3><span className="hx-cnt">{read.length}</span></div>
      <ul className="press">
        {read.map((l) => {
          const path = l.signalId ? conversationPath(l.signalId, l.open ?? null, storeId, l.kind ?? undefined) : null;
          return (
            <li key={l.id} className="press-row">
              <span className="press-said">{path ? <a href={path} onClick={(e) => { e.preventDefault(); openPath(path); }}>{l.text}</a> : l.text}</span>
              {l.at ? <span className="press-src">{formatDay(l.at)}</span> : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function Press({ press, storeId, openPath }: { press: CompanyRecordView['press']; storeId: string; openPath: (path: string) => void }) {
  const [all, setAll] = useState(false);
  const read = <ReadLines read={press?.read ?? []} storeId={storeId} openPath={openPath} />;
  if (press && press.named === 0 && (press.read?.length ?? 0) > 0) return <Section id="a-press" title="Press and intelligence">{read}</Section>;
  if (!press || press.named === 0) {
    return (
      <Section id="a-press" title="Press and intelligence" state="Nothing about them">
        <Nothing read="Read: every article since Cobrain began reading the industry, and every message, chat and meeting Cobrain has read for what it means to you.">
          Nothing Cobrain has read names this company.
        </Nothing>
      </Section>
    );
  }
  const looked = `${plural(press.named, 'item')} named them${press.newestAt ? `, the newest on ${formatDay(press.newestAt)}` : ''}${press.hollow ? `, and on ${press.hollow === 1 ? 'one of them' : `${press.hollow} of them`} Cobrain found nothing bearing on them` : ''}`;
  if (press.items.length === 0) {
    return (
      <Section id="a-press" title="Press and intelligence" state={`${press.named} naming them, none drawn from`}>
        <Nothing read="Read: every article, message, chat and meeting Cobrain has read for what it means to you.">
          {looked} — so there is nothing here worth your reading time yet.
        </Nothing>
        {read}
      </Section>
    );
  }
  return (
    <Section id="a-press" title="Press and intelligence" state={`${countWord(press.drawn)} worth reading of ${press.named}`}>
      <ul className="press">
        {(all ? press.items : press.items.slice(0, PRESS_FIRST)).map((it) => {
          // REQ-2026-10-07-167: every item opens. An article at its address; a mail on the thread page; a meeting or a
          // chat on its own page. Where the item came from is named with its title or subject, never "an email" alone.
          const path = it.url ? null : it.signalId ? conversationPath(it.signalId, it.open ?? null, storeId, it.kind) : null;
          const named = it.title ?? it.subject ?? null;
          const label = named ?? (it.url ? 'the article' : sourceWord(it.kind));
          const meta = [named && !it.url ? sourceWord(it.kind) : null, it.publisher, it.at ? formatDay(it.at) : null].filter(Boolean);
          return (
            <li key={it.id} className="press-row">
              <span className="press-said">{it.implication}</span>
              {/* The words of the source that name them, so the reading can be checked against what was said. */}
              {it.passage ? <span className="press-passage">&ldquo;{it.passage}&rdquo;</span> : null}
              <span className="press-src">
                {it.url
                  ? <a href={it.url} target="_blank" rel="noreferrer">{label}</a>
                  : path ? <a href={path} onClick={(e) => { e.preventDefault(); openPath(path); }}>{label}</a> : label}
                {meta.map((m) => `, ${m}`).join('')}
              </span>
            </li>
          );
        })}
      </ul>
      {press.items.length > PRESS_FIRST
        ? <p className="dw-addlink"><ActionButton reading type="button" className="hx-link" aria-expanded={all} onClick={() => setAll((v) => !v)}>{all ? 'Show fewer' : press.drawn > press.items.length ? `Show the newest ${press.items.length}` : `Show all ${press.items.length}`}</ActionButton></p>
        : null}
      {/* BUGS.md D136: the heading counts every reading worth it; where the record carries fewer, it says how many of them. */}
      {press.drawn > (all ? press.items.length : Math.min(PRESS_FIRST, press.items.length))
        ? <p className="note press-listed">{all ? `The newest ${press.items.length} of ${press.drawn} worth reading are listed.` : `The newest ${Math.min(PRESS_FIRST, press.items.length)} of ${press.drawn} worth reading are shown.`}</p>
        : null}
      <p className="none">
        {looked}.
        <span className="read">Read: every article, message, chat and meeting Cobrain has read for what it means to you. Only what it found bearing on them is shown.</span>
      </p>
      {read}
    </Section>
  );
}

/**
 * Where a reading came from, in the reader's words. A reading drawn from the team's own
 * correspondence is most of what this section holds -- on Bet365, 42 of the 48 worth reading -- and
 * calling all of it "press" would be a lie about where Cobrain had been looking.
 */
function sourceWord(kind: string): string {
  const words: Record<string, string> = {
    news_item: 'an article', email: 'an email', meeting: 'a meeting', teams_chat: 'a Teams chat',
    teams_channel: 'a Teams channel', calendar_event: 'a calendar entry', call: 'a call',
  };
  return words[kind] ?? 'something Cobrain read';
}

/**
 * Money for one of this company's own agreements that Anaplan books against ANOTHER company (A37).
 *
 * Bet365's register carries "Content Licencing (MODUS)". Anaplan holds Modus as a customer in its
 * own right, and module 15 move 2 confirmed that name to a company page of its own on 15 September.
 * So Bet365's page read "finance booked £11,500" while money for one of its four live agreements sat
 * under another name, and neither page said a word about it. Mike, on being shown it: "you cannot
 * have anomalies like this, you need to map everything to the billing."
 *
 * The page cannot resolve it -- whether that revenue is the gross billed here or the net after what
 * is paid away is finance's to say -- but it can refuse to under-report a client in silence.
 */
function Elsewhere({ rows, go }: { rows: CompanyRecordView['elsewhere']; go: (path: string, label: string) => ReactNode }) {
  if (!rows || rows.length === 0) return null;
  const gbp = (n: number) => `£${Math.round(n).toLocaleString('en-GB')}`;
  return (
    <Section id="a-elsewhere" title="Money booked somewhere else" state={`${plural(rows.length, 'agreement')} of theirs`}>
      <ul className="elsewhere">
        {rows.map((r) => (
          <li key={r.contractId} className="else-row">
            <span className="else-what">Their agreement <b>{r.product}</b> names <b>{r.named}</b>.</span>
            <span className="else-where">
              Finance bills <b>{r.anaplanCustomer}</b> as a customer of its own, and Cobrain has that name joined to {go(`/companies/${r.companyId}`, r.company)}
              {r.billedLast12 !== null ? <>, where it booked <b>{gbp(r.billedLast12)}</b> in twelve months</> : null}. None of it counts towards this company.
            </span>
            <span className="else-ask">Whether that is money billed to this client under another name is finance&rsquo;s to settle. Until they do, what this page shows for them is short by it.</span>
          </li>
        ))}
      </ul>
    </Section>
  );
}
